<!-- Copyright (c) 2024 Digital Systems (M) Sdn. Bhd. -->

<template>
  <div class="print-AR-invoice-main">
    <div class="AR-invoice-options">
      <Input
        :inputValue.sync="attnInputValue"
        placeholder="Attn To."
        :isDisabled="previewDocumentRelatedControlsIsDisabled"
      />
      <!-- =============== move Attn below? to be next to all the other controls related to preview invoice? -->
      <div class="invoice-filters-and-table-box-controls">
        <div class="invoice-filters-summary-container">
          <span>{{ invoiceFiltersSummary }}</span>
        </div>
        <ButtonFilter
          buttonText="Change Filter"
          filterPosition="right"
          :filterWidth="420"
          :isOpen.sync="invoiceFiltersIsOpen"
          :buttonIsDisabled="invoiceFiltersIsDisabled"
        >
          <div class="button-filter-content-container">
            <BoxContainer
              class="date-filter-container"
              :title="`${capitalisedDocumentNameBringPrinted} Date`"
              :isCheckBox="true"
              :isChecked.sync="invoiceDateIsChecked"
            >
              <DateTimePicker
                :placeholder="`Start ${capitalisedDocumentNameBringPrinted} Date`"
                :value.sync="invoiceDateStartValue"
                :showTime="false"
                :isRequired="invoiceDateIsChecked"
                :errorMessage.sync="invoiceDateStartErrorMessage"
                :isDisabled="!invoiceDateIsChecked"
              />
              <DateTimePicker
                :placeholder="`End ${capitalisedDocumentNameBringPrinted} Date`"
                :value.sync="invoiceDateEndValue"
                :showTime="false"
                :isRequired="invoiceDateIsChecked"
                :errorMessage.sync="invoiceDateEndErrorMessage"
                :isDisabled="!invoiceDateIsChecked"
              />
            </BoxContainer>
            <BoxContainer
              class="invoice-no-filter-container"
              :title="`${capitalisedDocumentNameBringPrinted} No.`"
              :isCheckBox="true"
              :isChecked.sync="invoiceNoIsChecked"
            >
              <InputQuery
                :placeholder="`Start ${capitalisedDocumentNameBringPrinted} No.`"
                :items="invoiceNoItems"
                itemKey="Invoice #"
                :showDesc="false"
                :selectedObject.sync="invoiceNoStartSelectedObject"
                :search.sync="invoiceNoStartSearch"
                :isRequired="invoiceNoIsChecked"
                :errorMessage.sync="invoiceNoStartErrorMessage"
                :isDisabled="!invoiceNoIsChecked"
              />
              <InputQuery
                :placeholder="`End ${capitalisedDocumentNameBringPrinted} No.`"
                :items="invoiceNoItems"
                itemKey="Invoice #"
                :showDesc="false"
                :selectedObject.sync="invoiceNoEndSelectedObject"
                :search.sync="invoiceNoEndSearch"
                :isRequired="invoiceNoIsChecked"
                :errorMessage.sync="invoiceNoEndErrorMessage"
                :isDisabled="!invoiceNoIsChecked"
              />
            </BoxContainer>
            <BoxContainer
              class="customer-filter-container"
              title="Customer"
              :isCheckBox="true"
              :isChecked.sync="customerIsChecked"
            >
              <InputQuery
                placeholder="Start Customer"
                :items="customerItems"
                itemKey="Customer Code"
                :showDesc="false"
                :selectedObject.sync="customerStartSelectedObject"
                :isRequired="customerIsChecked"
                :errorMessage.sync="customerStartErrorMessage"
                :isDisabled="!customerIsChecked"
              />
              <InputQuery
                placeholder="End Customer"
                :items="customerItems"
                itemKey="Customer Code"
                :showDesc="false"
                :selectedObject.sync="customerEndSelectedObject"
                :isRequired="customerIsChecked"
                :errorMessage.sync="customerEndErrorMessage"
                :isDisabled="!customerIsChecked"
              />
            </BoxContainer>
            <div class="outstanding-and-job-type-filters">
              <Dropdown
                class="outstanding-dropdown"
                placeholder="Outstanding"
                :items="outstandingItems"
                itemKey="name"
                :selectedObject.sync="outstandingSelectedObject"
              />
              <Dropdown
                class="job-type-dropdown"
                :placeholder="`${capitalisedDocumentNameBringPrinted} Job Type`"
                :items="invoiceJobTypeItems"
                itemKey="Description"
                :selectedObject.sync="invoiceJobTypeSelectedObject"
              />
            </div>
            <div class="not-printed-filter">
              <CheckBoxV2 title="Show Only Not Printed" :isChecked.sync="showOnlyNotPrintedIsChecked" />
            </div>
            <div class="refresh-button-container">
              <button @click="refreshClicked"> Refresh </button>
            </div>
          </div>
        </ButtonFilter>
        <button
          class="select-deselect-all-button"
          :disabled="selectDeselectAllButtonIsDisabled"
          @click="selectDeselectAllClicked"
        >
          {{ selectDeselectAllButtonText }}
        </button>
      </div>
      <div class="invoices-table-box-container">
        <TableBox
          :title="capitalisedDocumentNameBringPrinted"
          :tableData.sync="invoiceTableData"
          :selectedRow.sync="invoiceSelectedRow"
          :maxSelection="-1"
          :dataType="invoiceDataType"
          :loading="invoiceLoading"
        />
      </div>
      <div class="container-formatting-controls">
        <Dropdown
          class="container-list-dropdown"
          placeholder="Container List"
          :items="containerListItems"
          itemKey="name"
          :selectedObject.sync="containerListSelectedObject"
          :isRequired="true"
          :errorMessage.sync="containerListErrorMessage"
          :isDisabled="previewDocumentRelatedControlsIsDisabled"
        />
        <Input
          class="max-cont-input"
          placeholder="Max Cont. #"
          :inputValue.sync="maxContNoInputValue"
          :isDisabled="previewDocumentRelatedControlsIsDisabled"
          validator="positiveInteger,3"
        />
      </div>
      <div class="delivery-type-and-currency-formatting-controls">
        <Dropdown
          class="delivery-type-dropdown"
          placeholder="Delivery Type"
          :items="deliveryTypeItems"
          itemKey="Description"
          :selectedObject.sync="deliveryTypeSelectedObject"
          :isDisabled="previewDocumentRelatedControlsIsDisabled"
        />
        <Dropdown
          class="currency-format-dropdown"
          placeholder="Currency Format"
          :items="currencyFormatItems"
          itemKey="name"
          :selectedObject.sync="currencyFormatSelectedObject"
          :isRequired="true"
          :errorMessage.sync="currencyFormatErrorMessage"
          :isDisabled="previewDocumentRelatedControlsIsDisabled"
        />
      </div>
      <div class="credit-term-and-pay-to-formatting-controls">
        <Dropdown
          v-if="showCreditTerm"
          class="credit-term-dropdown"
          placeholder="Credit Term"
          :items="creditTermItems"
          itemKey="creditTerm"
          :selectedObject.sync="creditTermSelectedObject"
          :isDisabled="previewDocumentRelatedControlsIsDisabled"
        />
      </div>
      <div class="header-formatting-controls">
        <div class="header-formatting-column">
          <CheckBoxV2
            title="Show Company Letter Head"
            :isChecked.sync="showCompanyLetterHeadIsChecked"
            :isDisabled="previewDocumentRelatedControlsIsDisabled"
          />
          <CheckBoxV2
            title="Show Header Info"
            :isChecked.sync="showHeaderInfoIsChecked"
            :isDisabled="previewDocumentRelatedControlsIsDisabled"
          />
          <CheckBoxV2
            title="Show Prepayment"
            :isChecked.sync="showPrepaymentIsChecked"
            :isDisabled="previewDocumentRelatedControlsIsDisabled"
          />
        </div>
        <div class="header-formatting-column">
          <CheckBoxV2
            title="Remark Print Next Line"
            :isChecked.sync="remarkPrintNextLineIsChecked"
            :isDisabled="previewDocumentRelatedControlsIsDisabled"
          />
          <CheckBoxV2
            v-if="showInternalCopyOnlyCheckbox"
            title="Internal Copy Only"
            :isChecked.sync="internalCopyOnlyIsChecked"
            :isDisabled="previewDocumentRelatedControlsIsDisabled"
          />
          <CheckBoxV2
            title="Show Enclosure"
            :isChecked.sync="showEnclosureChecked"
            :isDisabled="previewDocumentRelatedControlsIsDisabled"
          />
        </div>
      </div>
      <div class="invoice-format-controls">
        <Dropdown
          class="format-dropdown"
          placeholder="Format"
          :items="formatItems"
          itemKey="name"
          :selectedObject.sync="formatSelectedObject"
          :isRequired="true"
          :errorMessage.sync="formatErrorMessage"
          :isDisabled="previewDocumentRelatedControlsIsDisabled"
        />
      </div>
      <div class="footer-image-controls">
        <InputQuery
          class="footer-image-dropdown"
          placeholder="Footer Image"
          :items="footerImageItems"
          itemKey="description"
          descKey="name"
          :showDesc="true"
          :selectedObject.sync="footerImageSelectedObject"
          :isRequired="true"
          :errorMessage.sync="footerImageErrorMessage"
          :isDisabled="previewDocumentRelatedControlsIsDisabled"
        />
      </div>
      <div class="call-to-action-buttons">
        <button @click="previewInvoiceClicked" :disabled="previewDocumentRelatedControlsIsDisabled"
          >Preview {{ capitalisedDocumentNameBringPrinted }}</button
        >
      </div>
    </div>
    <div class="AR-invoice-preview">
      <DesignerV3
        v-if="showDesignerV3"
        :reportFiles="designerV3ReportFiles"
        :reportData="designerV3ReportData"
        :sharedData="designerV3SharedData"
        :canToggleBackground="false"
        :canShare="true"
        :shareBehaviour="designerV3ShareBehaviour"
        :canClose="false"
        :downloadName="downloadFileName"
        @shareClicked="designerV3ShareClicked"
        @afterPrint="designerV3AfterPrint"
        ref="designerV3"
      />
      <div v-else class="user-instructions">
        <span
          >Select some {{ documentNameBringPrinted }}s and click on
          <strong>Preview {{ capitalisedDocumentNameBringPrinted }}</strong> to get started</span
        >
      </div>
    </div>
    <PopupV2
      class="email-invoice-popup"
      :show="showEmailInvoicePopup"
      icon="https://dsmlib.logistics.systems/Icons/BlackMail.png"
      :buttons="emailInvoicePopupButtons"
      title="Email Invoice"
      width="60%"
      @buttonClicked="emailInvoicePopupButtonClicked"
    >
      <div class="email-invoice-popup-content-container">
        <div class="mail-inputs-container">
          <div class="email-input-container">
            <EmailInput :mails.sync="emailInvoicePopupToMails" title="To" />
          </div>
          <div class="email-input-container">
            <EmailInput :mails.sync="emailInvoicePopupCCMails" title="CC">
              <span class="note">Your email will be automatically included in the CC</span>
            </EmailInput>
          </div>
          <div class="email-input-container">
            <EmailInput :mails.sync="emailInvoicePopupBCCMails" title="BCC" />
          </div>
        </div>
        <div class="email-content-inputs-container">
          <Input :inputValue.sync="emailInvoicePopupSubjectInputValue" placeholder="Subject" validator="text,100" />
          <RemarksBox title="Message" :text.sync="emailInvoicePopupMessageText" :maxLength="850" />
        </div>
      </div>
    </PopupV2>
  </div>
</template>

<script>
// ================== may need a dropdown for companies like TS to select which company header image to use

// ================== need to design a container attachment sheet template for invoices with too many containers
// ================== mainly used by MAX

// ================== need API to return currency format based on the invoice and the currency dropdown selection
// ================== things that will change
// ==================     - currency label (RM --> USD)
// ==================     - charge code amounts
// ==================     - charge code subtotal and grand totals
// ==================     - receipt amount
// ==================     - total amount in words
// ================== things that won't change
// ==================     - amounts in the service tax exemption clause

export default {
  components: {},
  computed: {
    documentTypeBeingPrinted: function () {
      return this.$route.path.endsWith('Print Invoice') ? '0' : '1';
      // txntype 0 is for invoices, 1 is for debit notes
      // it's either one of the two
    },
    documentNameBringPrinted: function () {
      return this.documentTypeBeingPrinted === '0' ? 'invoice' : 'debit note';
    },
    capitalisedDocumentNameBringPrinted: function () {
      return this.documentTypeBeingPrinted === '0' ? 'Invoice' : 'Debit Note';
    },
    selectDeselectAllButtonIsDisabled: function () {
      return this.invoiceTableData.length === 0;
    },
    selectDeselectAllButtonText: function () {
      if (this.invoiceTableData.length === 0) {
        return 'Select All';
      }

      return this.invoiceTableData.length !== this.invoiceSelectedRow.length ? 'Select All' : 'Deselect All';
    },
    showCreditTerm: function () {
      return this.creditTermItems.length > 0;
    },
    enclosureLocWords: function () {
      let location = '';
      switch (this.invoiceEnclosureLoc) {
        case '0':
          location = 'topOfHeaderInfo'; // default
          break;

        case '1':
          location = 'aboveChargesList';
          break;

        case '2':
          location = 'belowChargesList';
          break;

        case '3':
          location = 'belowGrandTotal';
          break;
      }
      return location;
    },
  },
  data: function () {
    return {
      userDetails: null,
      printedBySelection: '0',
      settingsSystemCoFooterFin2: '',
      settingsSystemCoFooterFin3: '',
      qtyDecimalPlaces: 3,
      unitPriceDecimalPlaces: 2,

      attnInputValue: '',

      //#region invoice filters
      invoiceFiltersSummary: '',
      invoiceFiltersIsOpen: false,
      invoiceFiltersIsDisabled: true,

      invoiceDateIsChecked: false,
      invoiceDateStartValue: null,
      invoiceDateStartErrorMessage: '',
      invoiceDateEndValue: null,
      invoiceDateEndErrorMessage: '',

      invoiceNoIsChecked: false,
      invoiceNoItems: [],
      invoiceNoStartSelectedObject: null,
      invoiceNoStartSearch: '',
      invoiceNoStartErrorMessage: '',
      invoiceNoEndSelectedObject: null,
      invoiceNoEndSearch: '',
      invoiceNoEndErrorMessage: '',

      customerIsChecked: false,
      customerStartSelectedObject: null,
      customerStartErrorMessage: '',
      customerEndSelectedObject: null,
      customerEndErrorMessage: '',
      customerItems: [],

      outstandingItems: [{ name: 'Full' }, { name: 'Partial' }],
      outstandingSelectedObject: null,

      invoiceJobTypeItems: [],
      invoiceJobTypeSelectedObject: null,

      showOnlyNotPrintedIsChecked: false,
      //#endregion

      //#region invoice table box
      invoiceTableData: [],
      invoiceSelectedRow: [],
      invoiceDataType: { 'Total Amount': 'currency' },
      invoiceLoading: false,
      //#endregion

      previewDocumentRelatedControlsIsDisabled: true,
      containerListItems: [
        { name: 'All Container related to Job', APIValue: 'job' },
        { name: 'All Container related to Invoice', APIValue: 'invoice' },
        { name: 'None', APIValue: '' },
      ],
      containerListSelectedObject: null,
      containerListErrorMessage: '',

      maxContNoInputValue: '',

      deliveryTypeItems: [],
      deliveryTypeSelectedObject: null,

      currencyFormatItems: [
        { name: 'Original Currency', APIValue: 'original' },
        { name: 'Local Currency', APIValue: 'MYR' }, // =============== hardcoded for now
      ],
      currencyFormatSelectedObject: null,
      currencyFormatErrorMessage: '',

      creditTermItems: [],
      creditTermSelectedObject: null,

      showCompanyLetterHeadIsChecked: false,
      showHeaderInfoIsChecked: false,
      showPrepaymentIsChecked: false,
      showEnclosureChecked: false,

      remarkPrintNextLineIsChecked: false,

      showInternalCopyOnlyCheckbox: false,
      internalCopyOnlyIsChecked: false,

      formatItems: [
        // hardcoded on the front-end for now
        { name: 'Non-Tax', APIValue: '0' },
        { name: 'Tax', APIValue: '1' },
        { name: 'Default', APIValue: '2' },
      ],
      formatSelectedObject: null,
      formatErrorMessage: '',

      footerImageItems: [
        { name: 'Image 1', description: '' },
        { name: 'Image 2', description: '' },
        { name: 'Image 3', description: '' },
      ],
      footerImageSelectedObject: null,
      footerImageErrorMessage: '',
      autoFooterConditions: '',

      showDesignerV3: false,
      designerV3ReportFiles: [],
      designerV3ReportData: [],
      designerV3SharedData: {},
      designerV3ShareBehaviour: {
        behaviour: 'uploadAndEmit',
      },
      downloadFileName: '',
      invoiceSignatureRequired: true, // populated based on SettingsSystem
      invoiceAddressFormat: 'NAPFT', // populated based on SettingsSystem, [C]ustCode, Cust[N]ame, [A]ddress, [P]hone, [F]ax, A[T]tention
      invoiceTaxExemptionFormat: '0', // populated based on SettingsSystem, 0 - textual, 1 - tabular
      invoiceEnclosureLoc: '0', // populated based on SettingsSystem, 0 - topOfHeaderInfo, 1 - aboveChargesList, 2 - belowChargesList, 3 - belowGrandTotal
      invoiceMultiJobTable: true, // populated based on SettingsSystem
      invoiceShowSendToAddress: false,
      invoiceHidePrintedBy: false, //populated based on SettingsSystem, 0 - false(show), 1 - true(hide)
      invoiceShowOwnSSTNo: false, //populated based on SettingsSystem, 0 - false(hide), 1 - true(show)
      invoiceShowMYRTotalCust: [],
      invoiceTMSDescriptionFormat: 'RDC',
      invoiceHMSShowExchRate: false,
      invoiceHMSShowBNL: false, //show booking ref no and lorry no
      invoiceHMSShowBLNoBookingRef: false, //show booking ref no (export) or BL No (import)

      showEmailInvoicePopup: false,
      emailInvoicePopupButtons: [{ text: 'Send Email' }, { text: 'Cancel', color: 'lightgray' }],
      emailInvoicePopupToMails: [],
      emailInvoicePopupCCMails: [],
      emailInvoicePopupBCCMails: [],
      emailInvoicePopupSubjectInputValue: '',
      emailInvoicePopupMessageText: '',
    };
  },
  props: {},
  methods: {
    refreshClicked: async function (_pointerEvent) {
      let error = false;

      if (this.invoiceDateIsChecked) {
        if (!this.invoiceDateStartValue) {
          this.invoiceDateStartErrorMessage = 'Start Invoice Date is required!';
          error = true;
        }

        if (!this.invoiceDateEndValue) {
          this.invoiceDateEndErrorMessage = 'End Invoice Date is required!';
          error = true;
        }
      }

      if (this.invoiceNoIsChecked) {
        if (!this.invoiceNoStartSelectedObject && !this.invoiceNoStartSearch.trim()) {
          this.invoiceNoStartErrorMessage = 'Start Invoice No. is required!';
          error = true;
        }

        if (!this.invoiceNoEndSelectedObject && !this.invoiceNoEndSearch.trim()) {
          this.invoiceNoEndErrorMessage = 'End Invoice No. is required!';
          error = true;
        }
      }

      if (this.customerIsChecked) {
        if (!this.customerStartSelectedObject) {
          this.customerStartErrorMessage = 'Start Customer is required!';
          error = true;
        }

        if (!this.customerEndSelectedObject) {
          this.customerEndErrorMessage = 'End Customer is required!';
          error = true;
        }
      }

      // ============== check the other dropdown for invalid values as well

      if (error) {
        return;
      }

      if (!this.invoiceDateIsChecked && !this.invoiceNoIsChecked) {
        const proceedConfirmation = await this.$showAlert(
          'warning',
          'This May Take A While',
          'You are not filtering by Invoice Date and Invoice No.\n\nLoading this data may take a while. Proceed?',
          [{ text: 'Proceed' }, { text: 'Cancel', color: 'lightgray' }],
        );

        if (proceedConfirmation === 'Cancel') {
          return;
        }
      }

      this.invoiceFiltersIsOpen = false;
      this.invoiceFiltersIsDisabled = true;
      this.updateInvoiceFiltersSummary();
      await this.getFilteredInvoiceList();
      this.invoiceFiltersIsDisabled = false;
    },
    updateInvoiceFiltersSummary: function () {
      // updates the summary text next to ButtonFilter
      const filtersList = []; // make sure there are no errors with the filter options before calling this method

      if (this.invoiceDateIsChecked) {
        filtersList.push(
          `date from ${this.invoiceDateStartValue.formatString('yyyy-MM-dd')} to ${this.invoiceDateEndValue.formatString('yyyy-MM-dd')}`,
        );
      }

      if (this.invoiceNoIsChecked) {
        let invoiceNoFilterSummary = `${this.documentNameBringPrinted} no. from `;

        if (this.invoiceNoStartSelectedObject) {
          invoiceNoFilterSummary += this.invoiceNoStartSelectedObject['Invoice #'];
        } else {
          invoiceNoFilterSummary += this.invoiceNoStartSearch;
        }

        invoiceNoFilterSummary += ' to ';

        if (this.invoiceNoEndSelectedObject) {
          invoiceNoFilterSummary += this.invoiceNoEndSelectedObject['Invoice #'];
        } else {
          invoiceNoFilterSummary += this.invoiceNoEndSearch;
        }

        filtersList.push(invoiceNoFilterSummary);
      }

      if (this.customerIsChecked) {
        filtersList.push(
          `customer from ${this.customerStartSelectedObject['Customer Code']} to ${this.customerEndSelectedObject['Customer Code']}`,
        );
      }

      if (this.outstandingSelectedObject) {
        filtersList.push(`${this.outstandingSelectedObject.name.toLowerCase()} outstanding invoices`);
      }

      if (this.invoiceJobTypeSelectedObject) {
        filtersList.push(`${this.invoiceJobTypeSelectedObject.Description} jobs`);
      }

      if (this.showOnlyNotPrintedIsChecked) {
        filtersList.push('invoices not printed yet');
      }

      this.invoiceFiltersSummary = `Filter by ${filtersList.join(', ')}`;
    },
    getFilteredInvoiceList: async function () {
      // calls the API to get the list of invoices that fulfill the criteria
      const requestBody = {}; // uses values in the ButtonFilter menu to formulate request body, make sure there are no errors before calling this method

      if (this.invoiceDateIsChecked) {
        requestBody.startdate = this.invoiceDateStartValue.formatString('yyyy-MM-dd');
        requestBody.enddate = this.invoiceDateEndValue.formatString('yyyy-MM-dd');
      } else {
        requestBody.startdate = '';
        requestBody.enddate = '';
      }

      if (this.invoiceNoIsChecked) {
        if (this.invoiceNoStartSelectedObject) {
          requestBody.startinvoiceno = this.invoiceNoStartSelectedObject['Invoice #'];
        } else {
          requestBody.startinvoiceno = this.invoiceNoStartSearch;
        }

        if (this.invoiceNoEndSelectedObject) {
          requestBody.endinvoiceno = this.invoiceNoEndSelectedObject['Invoice #'];
        } else {
          requestBody.endinvoiceno = this.invoiceNoEndSearch;
        }
      } else {
        requestBody.startinvoiceno = '';
        requestBody.endinvoiceno = '';
      }

      if (this.customerIsChecked) {
        requestBody.startcustomer = this.customerStartSelectedObject['Customer Code'];
        requestBody.endcustomer = this.customerEndSelectedObject['Customer Code'];
      } else {
        requestBody.startcustomer = '';
        requestBody.endcustomer = '';
      }

      switch (this.outstandingSelectedObject?.name) {
        case 'Full':
          requestBody.outstanding = '1';
          break;

        case 'Partial':
          requestBody.outstanding = '2';
          break;

        default:
          requestBody.outstanding = '0';
          break;
      }

      if (this.invoiceJobTypeSelectedObject) {
        requestBody.invoicejobtype = this.invoiceJobTypeSelectedObject['Job Type ID'];
      } else {
        requestBody.invoicejobtype = '';
      }

      requestBody.notprinted = this.showOnlyNotPrintedIsChecked ? '1' : '0';
      requestBody.txntype = this.documentTypeBeingPrinted;

      this.invoiceTableData = [];
      this.invoiceSelectedRow = [];

      this.invoiceLoading = true;
      this.invoiceTableData = await this.$apiCallV2('/PrintARInvoice/PrintInvoiceFilter', requestBody);
      if (this.invoiceTableData.length === 1) {
        this.$nextTick(() => {
          // wait for TableBox to emit row IDs first before selecting the row
          this.invoiceSelectedRow = [...this.invoiceTableData];
        });
      }

      this.invoiceLoading = false;
    },
    selectDeselectAllClicked: function (_pointerEvent) {
      if (this.invoiceTableData.length !== this.invoiceSelectedRow.length) {
        this.invoiceSelectedRow = [];
        this.$nextTick(() => {
          this.invoiceSelectedRow = [...this.invoiceTableData];
        });
      } else {
        this.invoiceSelectedRow = [];
      }
    },
    previewInvoiceClicked: async function (_pointerEvent) {
      if (this.invoiceSelectedRow.length === 0) {
        this.$showAlert('error', 'No Invoice Selected', 'Please select at least one invoice to preview', [
          { text: 'OK' },
        ]);
        return;
      }

      let error = false;

      if (!this.containerListSelectedObject) {
        this.containerListErrorMessage = 'Container List is required!';
        error = true;
      }

      if (!this.currencyFormatSelectedObject) {
        this.currencyFormatErrorMessage = 'Currency Format is required!';
        error = true;
      }

      if (!this.formatSelectedObject) {
        this.formatErrorMessage = 'Format is required!';
        error = true;
      }

      if (!this.footerImageSelectedObject) {
        this.footerImageErrorMessage = 'Footer Image is required!';
        error = true;
      }

      if (error) {
        return;
      }

      const invoiceNumbers = this.invoiceSelectedRow
        .filter((invoice) => invoice['Inv No'])
        .map((invoice) => invoice['Inv No'])
        .join(',');
      this.$fullscreenLoading('Getting Billing Formats');
      const invoiceBillingFmt = await this.$apiCallV2('/PrintARInvoice/BillingFmt', {
        invno: invoiceNumbers,
        txntype: this.documentTypeBeingPrinted,
      });
      // there's another API call below, don't hide the loading screen first

      const templateFileNames = [];
      const formattedInvoiceDetails = [];
      let generalInvoiceDetails = null; // ============== need to declare this here for now, because the GetARInvoiceDetails is called repeatedly for every single selected invoice
      // =============== the for loop below will assign one of the general details returned by the API into the variable
      // =============== consider changing the API in the future so that the API only needs to be called once, then this variable won't have to be declared here

      for (const selectedRow of this.invoiceSelectedRow) {
        // determine how the charged are listed by for the current invoice based on the billing format
        let chargesListBy;

        const billingFmtInvoice = invoiceBillingFmt.find((fmt) => fmt.invno === selectedRow['Inv No']);
        switch (billingFmtInvoice?.filename) {
          case '2':
            chargesListBy = 'jobs'; // TMS Column uses jobs
            break;

          case '3':
            chargesListBy = 'chargestms';
            break;

          case '5':
            chargesListBy = 'chargeshms';
            break;

          case '6':
            chargesListBy = 'consignments';
            break;

          default:
            chargesListBy = 'charges'; // Default to 'charges' if no specific filename is found
            break;
        }

        const requestBody = {
          attnto: this.attnInputValue,

          invoicenoarray: selectedRow['Inv No'],

          containeroption: this.containerListSelectedObject.APIValue,
          maxcont: this.maxContNoInputValue,
          deliverytype: this.deliveryTypeSelectedObject?.['Delivery Type']?.trim?.() ?? '',
          currency: this.currencyFormatSelectedObject.APIValue,

          showcompanyletterhead: this.showCompanyLetterHeadIsChecked ? '1' : '0',
          showheaderinfo: this.showHeaderInfoIsChecked ? '1' : '0',
          showprepayment: this.showPrepaymentIsChecked ? '1' : '0',
          remarkprintnextline: this.remarkPrintNextLineIsChecked ? '1' : '0',

          format: '',
          txntype: this.documentTypeBeingPrinted,
          company: this.userDetails.CoName,
          coshortname: this.userDetails.CoRefname,
          gstformat: this.formatSelectedObject.APIValue,

          chargeslistby: chargesListBy,
        };

        this.$fullscreenLoading('Getting Invoice Details');
        const response = await this.$apiCallV2('/PrintARInvoice/GetARInvoiceDetails', requestBody);
        if (Array.isArray(response) && response[0].Results !== '0') {
          this.$fullscreenLoading();
          this.$showAlert('error', 'Error', response[0]['ErrMsg'], [{ text: 'OK' }]);
          return;
        }

        const invoiceDetails = response.PrintInvoice;

        if (!generalInvoiceDetails) {
          generalInvoiceDetails = invoiceDetails.General;
          generalInvoiceDetails.companyheader = this.userDetails['CoHeaderFin'] ? this.userDetails['CoHeaderFin'] : generalInvoiceDetails.companyheader;
          // =============== only assign one of the general details returned by the API into the variable
          // =============== consider changing the API in the future so that the API only needs to be called once, then this if statement won't be needed
        }

        this.$fullscreenLoading('Preparing to Print');

        //format date related key values to preferred datetime
        let invoice = invoiceDetails.Invoice[0]; // there should only be one file, since the API is being repeatedly called for every selected invoice
        invoice.Date =
          invoice.Date ? new Date(invoice.Date).formatString(this.$store.state['preferredDateFormat']) : '';
        invoice.Date_ETA =
          invoice.Date_ETA ? new Date(invoice.Date_ETA).formatString(this.$store.state['preferredDateFormat']) : '';
        invoice.JobDate =
          invoice.JobDate ? new Date(invoice.JobDate).formatString(this.$store.state['preferredDateFormat']) : '';
        invoice.etadate =
          invoice.etadate ? new Date(invoice.etadate).formatString(this.$store.state['preferredDateFormat']) : '';
        invoice.requireddate =
          invoice.requireddate ?
            new Date(invoice.requireddate).formatString(this.$store.state['preferredDateFormat'])
          : '';

        //format weight and volumne related key values to preferred format
        invoice.JobGWeight = this.$weightFormat(invoice.JobGWeight);
        invoice.JobMeasurement = this.$volumeFormat(invoice.JobMeasurement);

        if (this.showEnclosureChecked && invoice.Inv_Doc && invoice.Inv_Doc.length > 0) {
          invoice._showEnclosure = true; //move from share data to here because need to check if each invoice have the Inv_Doc array
        }

        //For FMG Adient Case
        if (this.userDetails.CoRefname == 'FMG') {
          invoice._FooterCompanyName = this.userDetails.CoName.toUpperCase();
        }

        //set the download file name for this invoice/debit note
        const invno = invoice.inv_no ?? '';
        let jobno = invoice.Job_no ?? '';
        let brn = invoice.BookingRefNo ?? '';
        let blno = invoice.BL_no ?? '';
        if (invoice.MultiJob_List && invoice.MultiJob_List.length > 1) {
          jobno = 'MultipleJobs';
          brn = '';
          blno = '';
        }
        filenameTemp = [invno, jobno, brn, blno].filter(Boolean).join('_');
        invoice._downloadFileName = `${filenameTemp}_${new Date().formatString('yyyyMMddHHmm')}`;

        if (invoice.MultiJob_List && invoice.MultiJob_List.length > 0) {
          invoice.MultiJob_List.forEach((multiJobList) => {
            multiJobList.date_eta =
              multiJobList.date_eta ?
                new Date(multiJobList.date_eta).formatString(this.$store.state['preferredDateFormat'])
              : '';
          });
        }

        if (invoice.RefInv && invoice.RefInv.length > 0) {
          invoice.RefInv.forEach((refInv) => {
            refInv.Date =
              refInv.Date ? new Date(refInv.Date).formatString(this.$store.state['preferredDateFormat']) : '';
          });
        }

        if (invoice.Receipt && invoice.Receipt.length > 0) {
          invoice.Receipt.forEach((r) => {
            r.ReceiptDate =
              r.ReceiptDate ? new Date(r.ReceiptDate).formatString(this.$store.state['preferredDateFormat']) : '';
          });
        }

        if (!invoice.Invoice_Charges || invoice.Invoice_Charges.length === 0) {
          // if the invoice was created without any charges
          formattedInvoiceDetails.push({}); // ignore this invoice, don't format anything
          templateFileNames.push('TaxInvoiceFMS.json'); // arbitrary decision to use the FMS invoice if no charges are added, it shouldn't ever hit this check anyways
          continue; // and move on to the next invoice
        }

        //format invoice_Charges key values to preferred datetime
        invoice.Invoice_Charges.forEach((invoiceCharge) => {
          invoiceCharge.jobdate =
            invoiceCharge.jobdate ?
              new Date(invoiceCharge.jobdate).formatString(this.$store.state['preferredDateFormat'])
            : '';
          invoiceCharge.deldate =
            invoiceCharge.deldate ?
              new Date(invoiceCharge.deldate).formatString(this.$store.state['preferredDateFormat'])
            : '';
        });

        const taxExemptionFormat = this.invoiceTaxExemptionFormat === '0' ? 'textual' : 'tabular'; // any format that needs this can use it freely
        // this.autoFooterConditions = 'billcu_no=3008/L14&Invoice_Charges.chargescode=RENTAL INC,SEC CHARGE:CoFooterFin_HW_Warehouse;CoFooterFin_HW_Others'; //for testing purpose
        if (this.footerImageSelectedObject.name.toUpperCase() == 'AUTO' && this.autoFooterConditions) {
          //check if settingssystem have value for the auto footer conditions
          const loopTemp = this.autoFooterConditions.split(';'); //split by semicolon. Semi colon is to seperate multiple condition
          let trueFalse;
          let autoFooter = '';
          loopTemp.forEach((loop) => {
            //loop each condition
            if (loop.includes('=')) {
              //Each condition will have the equal sign.
              const conditions = loop.split('&'); //some codition can have additional checking so uses the & symbol to combine
              conditions.forEach((c) => {
                const keyValues = c.split(':'); //split by colon. the first is the condition. The second is the footer image need to use
                const key = keyValues[0].split('=')[0]; //split by colon. The first is the key
                const values = keyValues[0].split('=')[1].split(','); //split by equal sign. The 2nd item is the values then can be split by comma
                const valuesUpperCase = values.map((m) => m.toUpperCase());
                if (key.includes == '.') {
                  //with . means that the checking is for array of values.
                  const keyObject = key.split('.')[0]; //the first value is the array name
                  const objectKeyName = key.split('.')[1]; //the key name in each object
                  if (
                    invoice[`${keyObject}`].some((s) => valuesUpperCase.includes(s[`${objectKeyName}`].toUpperCase()))
                  ) {
                    trueFalse = true;
                    autoFooter = keyValues[1];
                  } else {
                    trueFalse = false;
                  }
                }
                if (valuesUpperCase.includes(invoice[`${key}`].toUpperCase())) {
                  //check if the invoice conditions matches
                  trueFalse = true;
                  autoFooter = keyValues[1];
                } else {
                  trueFalse = false;
                }
              });
            } else if (!trueFalse) {
              //For the last condition, there should be no equal sign because it store the default footer image use
              autoFooter = loop;
            }
          });
          invoice._footer = `https://dsmapi.logistics.systems/logo/${autoFooter}.png`;
        }

        switch (
          billingFmtInvoice?.filename // determine which template to use
        ) {
          // case "1": // 1 - FRE
          //   break;

          case '2': {
            // 2 - TMS Column
            templateFileNames.push('TaxInvoiceTMSColumn.json');

            const formattedCustomerDetails = this.formatInvoiceHeaderCustomerDetails(
              invoice,
              this.attnInputValue,
              this.invoiceAddressFormat,
            );
            const formattedInvoiceHeader = await this.formatInvoiceHeaderForGeneralInvoiceTemplates(
              invoice,
              formattedCustomerDetails,
              this.creditTermSelectedObject,
              { hidePrintedBy: true },
            ); // ============== the TMS column format only displays the printed by field for a single customer, and not sure if they are still using the system, permanently disable it for now
            const formattedHeaderInfo = this.formatHeaderInfoForGeneralTMSColumnInvoiceTemplates(
              invoice,
              this.showHeaderInfoIsChecked,
              { enclosureLocation: this.enclosureLocWords },
            );
            const formattedInvoiceCharges = this.formatInvoiceChargesForGeneralTMSColumnTaxInvoiceTemplate(invoice);
            const formattedReceipts = this.formatReceiptsForGeneralInvoiceTemplates(
              invoice,
              this.showPrepaymentIsChecked,
            );
            const formattedTaxExemption = this.formatTaxExemptionForGeneralInvoiceTemplates(invoice, {
              taxExemptionFormat,
            });
            const formattedRefInvoices = this.formatRefInvoiceForGeneralInvoiceTemplates(invoice);

            invoice = {
              ...invoice,
              ...formattedInvoiceHeader,
              ...formattedHeaderInfo,
              ...formattedInvoiceCharges,
              ...formattedReceipts,
              ...formattedTaxExemption,
              ...formattedRefInvoices,
            };

            break;
          }

          case '3': {
            // 3 - TMS
            templateFileNames.push('TaxInvoiceTMS.json');

            const formattedCustomerDetails = this.formatInvoiceHeaderCustomerDetails(
              invoice,
              this.attnInputValue,
              this.invoiceAddressFormat,
            );
            const formattedInvoiceHeader = await this.formatInvoiceHeaderForGeneralInvoiceTemplates(
              invoice,
              formattedCustomerDetails,
              this.creditTermSelectedObject,
              { hidePrintedBy: true },
            ); // the TMS format has the printed by field permanently disabled for some reason, but it's implemented in case it's needed in the future
            const formattedHeaderInfo = this.formatHeaderInfoForGeneralTMSInvoiceTemplates(
              invoice,
              this.showHeaderInfoIsChecked,
              { enclosureLocation: this.enclosureLocWords },
            );
            const formattedInvoiceCharges = this.formatInvoiceChargesForGeneralTMSTaxInvoiceTemplate(invoice);
            const foreignCurrencyConversionDetails =
              this.formatForeignCurrencyConversionDetailsForGeneralInvoiceTemplates(
                invoice,
                this.currencyFormatSelectedObject.name,
              );
            const formattedReceipts = this.formatReceiptsForGeneralInvoiceTemplates(
              invoice,
              this.showPrepaymentIsChecked,
            );
            const formattedTaxExemption = this.formatTaxExemptionForGeneralInvoiceTemplates(invoice, {
              taxExemptionFormat,
            });
            const formattedRefInvoices = this.formatRefInvoiceForGeneralInvoiceTemplates(invoice);

            invoice = {
              ...invoice,
              ...formattedInvoiceHeader,
              ...formattedHeaderInfo,
              ...formattedInvoiceCharges,
              ...foreignCurrencyConversionDetails,
              ...formattedReceipts,
              ...formattedTaxExemption,
              ...formattedRefInvoices,
            };

            break;
          }

          // case "4": // 4 - column format - pallet
          //   break;

          case '5': {
            // 5 - HMS
            templateFileNames.push('TaxInvoiceHMS.json');

            const formattedCustomerDetails = this.formatInvoiceHeaderCustomerDetails(
              invoice,
              this.attnInputValue,
              this.invoiceAddressFormat,
            );
            const formattedInvoiceHeader = await this.formatInvoiceHeaderForGeneralInvoiceTemplates(
              invoice,
              formattedCustomerDetails,
              this.creditTermSelectedObject,
              { hidePrintedBy: true },
            ); // ============== the HMS format only displays the printed by field for a single customer, will need a settings system for controlling whether it's displayed, and another for controlling what the label says
            const formattedHeaderInfo = this.formatHeaderInfoForGeneralHMSInvoiceTemplates(
              invoice,
              this.showHeaderInfoIsChecked,
              { enclosureLocation: this.enclosureLocWords, showSendToAddress: this.invoiceShowSendToAddress },
            );
            const formattedContainerDetails = this.formatContainerDetailsForGeneralInvoiceTemplates(invoice);
            const formattedInvoiceCharges = this.formatInvoiceChargesForGeneralHMSTaxInvoiceTemplate(
              invoice,
              this.currencyFormatSelectedObject.name,
              this.invoiceHMSShowExchRate,
              this.remarkPrintNextLineIsChecked,
            );
            const foreignCurrencyConversionDetails =
              this.formatForeignCurrencyConversionDetailsForGeneralInvoiceTemplates(
                invoice,
                this.currencyFormatSelectedObject.name,
              );
            const formattedReceipts = this.formatReceiptsForGeneralInvoiceTemplates(
              invoice,
              this.showPrepaymentIsChecked,
            );
            const formattedTaxExemption = this.formatTaxExemptionForGeneralInvoiceTemplates(invoice, {
              taxExemptionFormat,
            });
            const formattedRefInvoices = this.formatRefInvoiceForGeneralInvoiceTemplates(invoice);

            invoice = {
              ...invoice,
              ...formattedInvoiceHeader,
              ...formattedHeaderInfo,
              ...formattedContainerDetails,
              ...formattedInvoiceCharges,
              ...foreignCurrencyConversionDetails,
              ...formattedReceipts,
              ...formattedTaxExemption,
              ...formattedRefInvoices,
            };

            break;
          }

          case '6': {
            // 6 - HMS Multi Job
            templateFileNames.push('TaxInvoiceHMSMultiJob.json'); // ================ template not working properly yet, need some changes to report designer

            const formattedCustomerDetails = this.formatInvoiceHeaderCustomerDetails(
              invoice,
              this.attnInputValue,
              this.invoiceAddressFormat,
            );
            const formattedInvoiceHeader = await this.formatInvoiceHeaderForGeneralInvoiceTemplates(
              invoice,
              formattedCustomerDetails,
              this.creditTermSelectedObject,
              { hidePrintedBy: true },
            ); // ============== the HMS format only displays the printed by field for a single customer, will need a settings system for controlling whether it's displayed, and another for controlling what the label says
            const formattedHeaderInfo = this.formatHeaderInfoForGeneralHMSMultiJobInvoiceTemplates(
              invoice,
              this.showHeaderInfoIsChecked,
              { enclosureLocation: this.enclosureLocWords },
            );
            const formattedInvoiceCharges = this.formatInvoiceChargesForGeneralHMSMultiJobTaxInvoiceTemplate(invoice);
            const formattedReceipts = this.formatReceiptsForGeneralInvoiceTemplates(
              invoice,
              this.showPrepaymentIsChecked,
            );
            const formattedTaxExemption = this.formatTaxExemptionForGeneralInvoiceTemplates(invoice, {
              taxExemptionFormat,
            });
            const formattedRefInvoices = this.formatRefInvoiceForGeneralInvoiceTemplates(invoice);

            invoice = {
              ...invoice,
              ...formattedInvoiceHeader,
              ...formattedHeaderInfo,
              ...formattedInvoiceCharges,
              ...formattedReceipts,
              ...formattedTaxExemption,
              ...formattedRefInvoices,
            };

            break;
          }

          // case "7": // 7 - Rebate
          //   break;

          // case "8": // 8 - Rebate (column format)
          //   break;

          case '0': // 0 - FMS
          default:
            if (
              this.formatSelectedObject.name === 'Non-Tax' ||
              (this.formatSelectedObject.name === 'Default' && invoice.GSTEffective === '0')
            ) {
              templateFileNames.push('NonTaxInvoiceFMS.json');

              switch (this.userDetails.CoRefname) {
                // =================== TNLS and TS should be able to reuse some of the logic from the common formats

                case 'TNLSTEST':
                case 'TNLS': {
                  let formattedCreditTerm;
                  if (this.creditTermSelectedObject) {
                    formattedCreditTerm = this.creditTermSelectedObject.creditTerm;
                  } else {
                    switch (invoice.CreditType) {
                      case 'C':
                        formattedCreditTerm = 'C.O.D.';
                        break;
                      case 'D':
                        if (invoice.CreditTerm && invoice.CreditTerm !== '0') {
                          formattedCreditTerm = `${invoice.CreditTerm} DAY${invoice.CreditTerm !== '1' ? 'S' : ''}`;
                        }
                        break;
                      case 'M':
                        if (invoice.CreditTerm && invoice.CreditTerm !== '0') {
                          formattedCreditTerm = `${invoice.CreditTerm} MONTH${invoice.CreditTerm !== '1' ? 'S' : ''}`;
                        }
                        break;
                    }
                  }

                  if (invoice.QRURL != '') {
                    invoice.QRImage = await this.$getQRURL(invoice.QRURL, { size: 150, margin: 10 });
                  }

                  invoice._invoiceHeader1 = [
                    {
                      leftLabel: 'Account To',
                      rightLabel: 'Invoice No.',
                      rightValue: invoice.inv_no,
                    },
                  ];

                  invoice._invoiceHeader2 = [
                    {
                      leftValue: invoice.cu_name,
                      rightLabel: 'Job No.',
                      rightValue: invoice.Job_no,
                    },
                    {
                      leftValue: invoice.cu_addr1,
                      rightLabel: 'A/C No.',
                      rightValue: invoice.billcu_no,
                    },
                    {
                      leftValue: invoice.cu_addr2,
                      rightLabel: 'Date',
                      rightValue: invoice.Date,
                    },
                  ];

                  if (invoice.cu_addr3 != '' && invoice.cu_addr4 != '') {
                    invoice._invoiceHeader2.push({
                      leftValue: `${invoice.cu_addr3}\n${invoice.cu_addr4}`,
                      rightLabel: 'Terms',
                      rightValue: formattedCreditTerm,
                    });
                  } else if (invoice.cu_addr3 != '' && invoice.cu_addr4 == '') {
                    invoice._invoiceHeader2.push({
                      leftValue: `${invoice.cu_addr3}\n${invoice.Postcode} ${invoice.City} ${invoice.State} ${invoice.Country}`,
                      rightLabel: 'Terms',
                      rightValue: formattedCreditTerm,
                    });
                  } else if (invoice.cu_addr3 == '' && invoice.cu_addr4 != '') {
                    invoice._invoiceHeader2.push({
                      leftValue: `${invoice.Postcode}\n${invoice.City} ${invoice.cu_addr4}`,
                      rightLabel: 'Terms',
                      rightValue: formattedCreditTerm,
                    });
                  } else {
                    invoice._invoiceHeader2.push({
                      leftValue: `${invoice.Postcode} ${invoice.City}\n${invoice.State} ${invoice.Country}`,
                      rightLabel: 'Terms',
                      rightValue: formattedCreditTerm,
                    });
                  }

                  invoice._invoiceHeader2.push({
                    leftValue: `Tel: ${invoice.cu_phone}  Fax: ${invoice.cu_fax}`,
                    rightLabel: 'Page',
                    rightValue: `{{jsreportPageNum}} of {{jsreportTotalPage}}`,
                  });

                  invoice._invoiceHeader3 = [
                    {
                      value: `Attn: ${this.attnInputValue.trim() || invoice.cu_contact}`,
                    },
                    {
                      value: `BRN: ${invoice.billcu_noregno}`,
                    },
                    {
                      value: `SST License No: ${invoice.billcu_nosst}`,
                    },
                    {
                      value: `TIN No.: ${invoice.billcu_notin}`,
                    },
                  ];

                  invoice.GoodsDescrptFmt = invoice.Commodity?.trim() || invoice.GoodsDescrpt;

                  if (invoiceDetails.General['Show Header Info'] !== '0') {
                    invoice._jobHeader1 = [
                      {
                        leftLabel: 'Origin/ Dest.:',
                        leftValue: `${invoice.Load_port} / ${invoice.Imp_port} ${invoice.Discharge_port}`,
                        rightLabel: 'Booking #',
                        rightValue: invoice.BookingRefNo,
                      },
                    ];

                    let ETLabel;
                    if (invoice.Job_Type == 'I') {
                      ETLabel = 'ETA';
                    } else if (invoice.Job_Type == 'E') {
                      ETLabel = 'ETD';
                    } else {
                      ETLabel = 'ETA';
                    }

                    const jobHeaderRow = {
                      leftLabel: '',
                      leftValue: '',
                      rightLabel: ETLabel,
                      rightValue: invoice.Date_ETA,
                    };

                    switch (invoice.TransMode) {
                      case 'S':
                        jobHeaderRow.leftLabel = 'B/L No.';
                        jobHeaderRow.leftValue = invoice.BL_no;
                        break;

                      case 'A':
                        jobHeaderRow.leftLabel = 'AWB No.';
                        // jobHeaderRow.leftValue = invoice.BL_no;
                        break;

                      case 'R':
                        jobHeaderRow.leftLabel = 'D/O No.';
                        jobHeaderRow.leftValue = invoice.DO_no;
                        break;

                      default:
                        jobHeaderRow.leftLabel = 'B/L No.';
                        jobHeaderRow.leftValue = invoice.BL_no;
                        break;
                    }

                    if (invoice.HBL_no) {
                      jobHeaderRow.leftLabel = 'HBL / B/L No.';
                      jobHeaderRow.leftValue = `${invoice.HBL_no} / ${invoice.BL_no}`;
                    }

                    invoice._jobHeader1.push(jobHeaderRow, {
                      leftLabel: 'Vessel / Voy',
                      leftValue: `${invoice.JobVessel_name} / ${invoice.VoyNo}`,
                      rightLabel: 'W T / M3',
                      rightValue: `${invoice.JobGWeight} KG / ${invoice.JobMeasurement} M3`,
                    });

                    // Add Disbursement
                    if (invoice.jobconfig == '6') {
                      invoice.Disbursement = 'Disbursement :';
                    }

                    if (invoice.Cont_List) {
                      let containerList;
                      if (this.maxContNoInputValue) {
                        containerList = invoice.Cont_List.slice(0, parseInt(this.maxContNoInputValue)); // the max cont no input already has a validator, no need to handle NaN
                      } else {
                        containerList = invoice.Cont_List;
                      }

                      invoice._containerHeader = [];

                      if (containerList.length > 0) {
                        const containerHeader = { containerLabel: 'Container No.' };

                        for (let i = 0; i < containerList.length; i++) {
                          const containerIndex = (i % 4) + 1; // Calculate container index (1 to 4)
                          const key = `Container${containerIndex}`;

                          // Initialize container entry if it doesn't exist
                          if (!containerHeader[key]) {
                            containerHeader[key] = [];
                          }

                          // Add container to the corresponding group
                          containerHeader[key].push(`${containerList[i].cont_no} (${containerList[i].cont_size}')`);
                        }

                        // Format the container groups for display
                        for (const key in containerHeader) {
                          if (Array.isArray(containerHeader[key])) {
                            containerHeader[key] = containerHeader[key].join('   ');
                          }
                        }

                        containerHeader.Container1 += ` (${invoice.TotalCont})`;
                        invoice._containerHeader.push(containerHeader);
                      }
                    }

                    //Validation for Remarks
                    const enclosures = [invoice.Enclosure1, invoice.Enclosure2, invoice.Enclosure3].filter(Boolean);
                    if (enclosures.length !== 0) {
                      invoice.remark = [
                        {
                          remarkLabel: 'Remark',
                          remarkValue: enclosures.join('\n'),
                        },
                      ];
                    }

                    if (invoice.GSTEffective == '1' && invoice.sumGSTTaxExempted != '0.00') {
                      invoice.B2BExemption = [
                        {
                          AmtExempted: invoice.sumGSTTaxExempted,
                        },
                      ];
                    }
                  }

                  invoice._invoiceChargesGroups = this.formatInvoiceChargesForGeneralTNLSTaxInvoiceTemplate(
                    invoice.Invoice_Charges,
                    invoiceDetails.General['Remark Print Next Line'] !== '0',
                  );

                  invoice._grandTotalExclTax = 0;
                  invoice._grandTotalTaxAmt = 0;
                  invoice._grandTotalInclTax = 0;

                  for (const invoiceChargesGroup of invoice._invoiceChargesGroups) {
                    invoice._grandTotalExclTax += invoiceChargesGroup.subTotalAmtExclTax;
                    invoice._grandTotalTaxAmt += invoiceChargesGroup.subTotalTaxAmt ?? 0;
                    invoice._grandTotalInclTax += invoiceChargesGroup.subTotalAmtInclTax;
                  }
                  invoice._grandTotalExclTax = this.$currencyFormat(invoice._grandTotalExclTax);
                  invoice._grandTotalTaxAmt = this.$currencyFormat(invoice._grandTotalTaxAmt);
                  invoice._grandTotalInclTax = this.$currencyFormat(invoice._grandTotalInclTax);
                  invoice.grandTotalInclTax = this.$currencyFormat(invoice.AmtInclTax_Total); // may not be the same as the underscored one, this one has been deducted with the prepayment qyvoon need to change

                  if (invoiceDetails.General['Show Pre Payment'] !== '0' && invoice?.Receipt?.length > 0) {
                    invoice._receipts = invoice.Receipt.map((receipt) => ({
                      receiptDescription: `LESS PREPAYMENT [RT #: ${receipt.ReceiptNo} Date: ${receipt.ReceiptDate} Chq#: ${receipt.ChequeNo} Bank: ${receipt.ChqBank}]`,
                      receiptAmt: this.$currencyFormat(receipt.Amount),
                    }));
                  }

                  invoice._showPrepayment =
                    invoiceDetails.General['Show Pre Payment'] !== '0' && invoice?.Receipt?.length > 0;

                  invoice._formattedSumGSTTaxExempted = this.$currencyFormat(invoice.sumGSTTaxExempted);
                  invoice._formattedTotalInclExemptedGST = this.$currencyFormat(invoice.TotalInclExemptedGST);

                  if (invoice.RefInv) {
                    invoice._PairedRefInv = []; // combine all ref invoices into pairs, because TS' format displays two ref invoices in a single line

                    for (let index = 0; index < invoice.RefInv.length; index++) {
                      if (index % 2 === 0) {
                        // even index
                        invoice._PairedRefInv.push({
                          // create a new pair
                          'Ref Invoice No 1': invoice.RefInv[index]['Ref Invoice No'],
                          'Date 1': invoice.RefInv[index].Date,
                          'Amount 1': this.$currencyFormat(invoice.RefInv[index].Amount),
                          'Ref Invoice No 2': '', // leave these blank for now
                          'Date 2': '', // filled in when index is odd
                          'Amount 2': '',
                        });
                      } else {
                        // odd index
                        const pairedRefInvIndex = Math.floor(index / 2); // update the pair created when index was even
                        invoice._PairedRefInv[pairedRefInvIndex]['Ref Invoice No 2'] =
                          invoice.RefInv[index]['Ref Invoice No'];
                        invoice._PairedRefInv[pairedRefInvIndex]['Date 2'] = invoice.RefInv[index].Date;
                        invoice._PairedRefInv[pairedRefInvIndex]['Amount 2'] = this.$currencyFormat(
                          invoice.RefInv[index].Amount,
                        );
                      }
                    }
                  }
                  break;
                }

                default: {
                  const formattedCustomerDetails = this.formatInvoiceHeaderCustomerDetails(
                    invoice,
                    this.attnInputValue,
                    this.invoiceAddressFormat,
                  );
                  const formattedInvoiceHeader = await this.formatInvoiceHeaderForGeneralInvoiceTemplates(
                    invoice,
                    formattedCustomerDetails,
                    this.creditTermSelectedObject,
                    { printedByValue: this.userDetails.UserName },
                  );
                  const formattedHeaderInfo = this.formatHeaderInfoForGeneralFMSInvoiceTemplates(
                    invoice,
                    this.showHeaderInfoIsChecked,
                    { enclosureLocation: this.enclosureLocWords, showMultiJobTable: this.invoiceMultiJobTable },
                  );
                  const formattedContainerDetails = this.formatContainerDetailsForGeneralInvoiceTemplates(invoice);
                  const formattedInvoiceCharges = this.formatInvoiceChargesForGeneralFMSNonTaxInvoiceTemplate(
                    invoice,
                    this.remarkPrintNextLineIsChecked,
                  );
                  const formattedReceipts = this.formatReceiptsForGeneralInvoiceTemplates(
                    invoice,
                    this.showPrepaymentIsChecked,
                  );
                  const formattedRefInvoices = this.formatRefInvoiceForGeneralInvoiceTemplates(invoice);

                  invoice = {
                    ...invoice,
                    ...formattedInvoiceHeader,
                    ...formattedHeaderInfo,
                    ...formattedContainerDetails,
                    ...formattedInvoiceCharges,
                    ...formattedReceipts,
                    ...formattedRefInvoices,
                  };

                  break;
                }
              }
            } else {
              templateFileNames.push('TaxInvoiceFMS.json');

              switch (this.userDetails.CoRefname) {
                // =================== TNLS and TS should be able to reuse some of the logic from the common formats

                case 'TNLSTEST':
                case 'TNLS': {
                  let formattedCreditTerm;
                  if (this.creditTermSelectedObject) {
                    formattedCreditTerm = this.creditTermSelectedObject.creditTerm;
                  } else {
                    switch (invoice.CreditType) {
                      case 'C':
                        formattedCreditTerm = 'C.O.D.';
                        break;
                      case 'D':
                        if (invoice.CreditTerm && invoice.CreditTerm !== '0') {
                          formattedCreditTerm = `${invoice.CreditTerm} DAY${invoice.CreditTerm !== '1' ? 'S' : ''}`;
                        }
                        break;
                      case 'M':
                        if (invoice.CreditTerm && invoice.CreditTerm !== '0') {
                          formattedCreditTerm = `${invoice.CreditTerm} MONTH${invoice.CreditTerm !== '1' ? 'S' : ''}`;
                        }
                        break;
                    }
                  }

                  if (invoice.QRURL != '') {
                    invoice.QRImage = await this.$getQRURL(invoice.QRURL, { size: 150, margin: 10 });
                  }

                  invoice._invoiceHeader1 = [
                    {
                      leftLabel: 'Account To',
                      rightLabel: 'Invoice No.',
                      rightValue: invoice.inv_no,
                    },
                  ];

                  invoice._invoiceHeader2 = [
                    {
                      leftValue: invoice.cu_name,
                      rightLabel: 'Job No.',
                      rightValue: invoice.Job_no,
                    },
                    {
                      leftValue: invoice.cu_addr1,
                      rightLabel: 'A/C No.',
                      rightValue: invoice.billcu_no,
                    },
                    {
                      leftValue: invoice.cu_addr2,
                      rightLabel: 'Date',
                      rightValue: invoice.Date,
                    },
                  ];

                  if (invoice.cu_addr3 != '' && invoice.cu_addr4 != '') {
                    invoice._invoiceHeader2.push({
                      leftValue: `${invoice.cu_addr3}\n${invoice.cu_addr4}`,
                      rightLabel: 'Terms',
                      rightValue: formattedCreditTerm,
                    });
                  } else if (invoice.cu_addr3 != '' && invoice.cu_addr4 == '') {
                    invoice._invoiceHeader2.push({
                      leftValue: `${invoice.cu_addr3}\n${invoice.Postcode} ${invoice.City} ${invoice.State} ${invoice.Country}`,
                      rightLabel: 'Terms',
                      rightValue: formattedCreditTerm,
                    });
                  } else if (invoice.cu_addr3 == '' && invoice.cu_addr4 != '') {
                    invoice._invoiceHeader2.push({
                      leftValue: `${invoice.Postcode}\n${invoice.City} ${invoice.cu_addr4}`,
                      rightLabel: 'Terms',
                      rightValue: formattedCreditTerm,
                    });
                  } else {
                    invoice._invoiceHeader2.push({
                      leftValue: `${invoice.Postcode} ${invoice.City}\n${invoice.State} ${invoice.Country}`,
                      rightLabel: 'Terms',
                      rightValue: formattedCreditTerm,
                    });
                  }

                  invoice._invoiceHeader2.push({
                    leftValue: `Tel: ${invoice.cu_phone}  Fax: ${invoice.cu_fax}`,
                    rightLabel: 'Page',
                    rightValue: `{{jsreportPageNum}} of {{jsreportTotalPage}}`,
                  });

                  invoice._invoiceHeader3 = [
                    {
                      value: `Attn: ${invoice.cu_contact}`,
                    },
                    {
                      value: `BRN: ${invoice.billcu_noregno}`,
                    },
                    {
                      value: `SST License No: ${invoice.billcu_nosst}`,
                    },
                    {
                      value: `TIN No.: ${invoice.billcu_notin}`,
                    },
                  ];

                  invoice.GoodsDescrptFmt = invoice.Commodity?.trim() || invoice.GoodsDescrpt;

                  if (invoiceDetails.General['Show Header Info'] !== '0') {
                    invoice._jobHeader1 = [
                      {
                        leftLabel: 'Origin/ Dest.:',
                        leftValue: `${invoice.Load_port} / ${invoice.Imp_port} ${invoice.Discharge_port}`,
                        rightLabel: 'Booking #',
                        rightValue: invoice.BookingRefNo,
                      },
                    ];

                    let ETLabel;
                    if (invoice.Job_Type == 'I') {
                      ETLabel = 'ETA';
                    } else if (invoice.Job_Type == 'E') {
                      ETLabel = 'ETD';
                    } else {
                      ETLabel = 'ETA';
                    }

                    const jobHeaderRow = {
                      leftLabel: '',
                      leftValue: '',
                      rightLabel: ETLabel,
                      rightValue: invoice.Date_ETA,
                    };

                    switch (invoice.TransMode) {
                      case 'S':
                        jobHeaderRow.leftLabel = 'B/L No.';
                        jobHeaderRow.leftValue = invoice.BL_no;
                        break;

                      case 'A':
                        jobHeaderRow.leftLabel = 'AWB No.';
                        // jobHeaderRow.leftValue = invoice.BL_no;
                        break;

                      case 'R':
                        jobHeaderRow.leftLabel = 'D/O No.';
                        jobHeaderRow.leftValue = invoice.DO_no;
                        break;

                      default:
                        jobHeaderRow.leftLabel = 'B/L No.';
                        jobHeaderRow.leftValue = invoice.BL_no;
                        break;
                    }

                    if (invoice.HBL_no) {
                      jobHeaderRow.leftLabel = 'HBL / B/L No.';
                      jobHeaderRow.leftValue = `${invoice.HBL_no} / ${invoice.BL_no}`;
                    }

                    invoice._jobHeader1.push(jobHeaderRow, {
                      leftLabel: 'Vessel / Voy',
                      leftValue: `${invoice.JobVessel_name} / ${invoice.VoyNo}`,
                      rightLabel: 'W T / M3',
                      rightValue: `${invoice.JobGWeight} KG / ${invoice.JobMeasurement} M3`,
                    });

                    // Add Disbursement
                    if (invoice.jobconfig == '6') {
                      invoice.Disbursement = 'Disbursement :';
                    }

                    if (invoice.Cont_List) {
                      let containerList;
                      if (this.maxContNoInputValue) {
                        containerList = invoice.Cont_List.slice(0, parseInt(this.maxContNoInputValue)); // the max cont no input already has a validator, no need to handle NaN
                      } else {
                        containerList = invoice.Cont_List;
                      }

                      invoice._containerHeader = [];

                      if (containerList.length > 0) {
                        const containerHeader = { containerLabel: 'Container No.' };

                        for (let i = 0; i < containerList.length; i++) {
                          const containerIndex = (i % 4) + 1; // Calculate container index (1 to 4)
                          const key = `Container${containerIndex}`;

                          // Initialize container entry if it doesn't exist
                          if (!containerHeader[key]) {
                            containerHeader[key] = [];
                          }

                          // Add container to the corresponding group
                          containerHeader[key].push(`${containerList[i].cont_no} (${containerList[i].cont_size}')`);
                        }

                        // Format the container groups for display
                        for (const key in containerHeader) {
                          if (Array.isArray(containerHeader[key])) {
                            containerHeader[key] = containerHeader[key].join('   ');
                          }
                        }

                        containerHeader.Container1 += ` (${invoice.TotalCont})`;
                        invoice._containerHeader.push(containerHeader);
                      }
                    }

                    //Validation for Remarks
                    const enclosures = [invoice.Enclosure1, invoice.Enclosure2, invoice.Enclosure3].filter(Boolean);
                    if (enclosures.length !== 0) {
                      invoice.remark = [
                        {
                          remarkLabel: 'Remark',
                          remarkValue: enclosures.join('\n'),
                        },
                      ];
                    }

                    if (invoice.GSTEffective == '1' && invoice.sumGSTTaxExempted != '0.00') {
                      invoice.B2BExemption = [
                        {
                          AmtExempted: invoice.sumGSTTaxExempted,
                        },
                      ];
                    }

                    if (invoice.sumGSTTaxExemptedS8E2 && invoice.sumGSTTaxExemptedS8E2 != '0.00') {
                      invoice.B2BExemptionS8E2 = true;
                    }
                  }

                  invoice._invoiceChargesGroups = this.formatInvoiceChargesForGeneralTNLSTaxInvoiceTemplate(
                    invoice.Invoice_Charges,
                    invoiceDetails.General['Remark Print Next Line'] !== '0',
                  );

                  invoice._grandTotalExclTax = 0;
                  invoice._grandTotalTaxAmt = 0;
                  invoice._grandTotalInclTax = 0;

                  for (const invoiceChargesGroup of invoice._invoiceChargesGroups) {
                    invoice._grandTotalExclTax += invoiceChargesGroup.subTotalAmtExclTax;
                    invoice._grandTotalTaxAmt += invoiceChargesGroup.subTotalTaxAmt ?? 0;
                    invoice._grandTotalInclTax += invoiceChargesGroup.subTotalAmtInclTax;
                  }
                  invoice._grandTotalExclTax = this.$currencyFormat(invoice._grandTotalExclTax);
                  invoice._grandTotalTaxAmt = this.$currencyFormat(invoice._grandTotalTaxAmt);
                  invoice._grandTotalInclTax = this.$currencyFormat(invoice._grandTotalInclTax);

                  if (invoiceDetails.General['Show Pre Payment'] !== '0' && invoice?.Receipt?.length > 0) {
                    invoice._receipts = invoice.Receipt.map((receipt) => ({
                      receiptDescription: `LESS PREPAYMENT [RT #: ${receipt.ReceiptNo} Date: ${receipt.ReceiptDate} Chq#: ${receipt.ChequeNo} Bank: ${receipt.ChqBank}]`,
                      receiptAmt: this.$currencyFormat(receipt.Amount),
                    }));
                  }

                  //   if (invoiceHasESVCharge) {
                  //     switch (this.invoiceTaxExemptionFormat) { // add if there's 'ESV'
                  //       case "0":
                  //         invoiceDetails.General._showTextualTaxExemptionFormat = true;
                  //         invoiceDetails.General._showTabularTaxExemptionFormat = false;
                  //         break;

                  //       case "1":
                  //         invoiceDetails.General._showTextualTaxExemptionFormat = false;
                  //         invoiceDetails.General._showTabularTaxExemptionFormat = true;
                  //         break;
                  //     }
                  //   } else {
                  //     invoiceDetails.General._showTextualTaxExemptionFormat = false;
                  //     invoiceDetails.General._showTabularTaxExemptionFormat = false;
                  //   }

                  invoice._showPrepayment =
                    invoiceDetails.General['Show Pre Payment'] !== '0' && invoice?.Receipt?.length > 0;

                  invoice._formattedSumGSTTaxExempted = this.$currencyFormat(invoice.sumGSTTaxExempted);
                  invoice._formattedTotalInclExemptedGST = this.$currencyFormat(invoice.TotalInclExemptedGST);

                  if (invoice.RefInv) {
                    invoice._PairedRefInv = []; // combine all ref invoices into pairs, because TS' format displays two ref invoices in a single line

                    for (let index = 0; index < invoice.RefInv.length; index++) {
                      if (index % 2 === 0) {
                        // even index
                        invoice._PairedRefInv.push({
                          // create a new pair
                          'Ref Invoice No 1': invoice.RefInv[index]['Ref Invoice No'],
                          'Date 1': invoice.RefInv[index].Date,
                          'Amount 1': this.$currencyFormat(invoice.RefInv[index].Amount),
                          'Ref Invoice No 2': '', // leave these blank for now
                          'Date 2': '', // filled in when index is odd
                          'Amount 2': '',
                        });
                      } else {
                        // odd index
                        const pairedRefInvIndex = Math.floor(index / 2); // update the pair created when index was even
                        invoice._PairedRefInv[pairedRefInvIndex]['Ref Invoice No 2'] =
                          invoice.RefInv[index]['Ref Invoice No'];
                        invoice._PairedRefInv[pairedRefInvIndex]['Date 2'] = invoice.RefInv[index].Date;
                        invoice._PairedRefInv[pairedRefInvIndex]['Amount 2'] = this.$currencyFormat(
                          invoice.RefInv[index].Amount,
                        );
                      }
                    }
                  }
                  break;
                }

                default: {
                  const formattedCustomerDetails = this.formatInvoiceHeaderCustomerDetails(
                    invoice,
                    this.attnInputValue,
                    this.invoiceAddressFormat,
                  );
                  const formattedInvoiceHeader = await this.formatInvoiceHeaderForGeneralInvoiceTemplates(
                    invoice,
                    formattedCustomerDetails,
                    this.creditTermSelectedObject,
                    { printedByValue: this.userDetails.UserName, hidePrintedBy: this.invoiceHidePrintedBy },
                  );
                  const formattedHeaderInfo = this.formatHeaderInfoForGeneralFMSInvoiceTemplates(
                    invoice,
                    this.showHeaderInfoIsChecked,
                    { enclosureLocation: this.enclosureLocWords, showMultiJobTable: this.invoiceMultiJobTable },
                  );
                  const formattedContainerDetails = this.formatContainerDetailsForGeneralInvoiceTemplates(invoice);
                  const formattedInvoiceCharges = this.formatInvoiceChargesForGeneralFMSTaxInvoiceTemplate(
                    invoice,
                    this.currencyFormatSelectedObject.name,
                    this.remarkPrintNextLineIsChecked,
                  );
                  const foreignCurrencyConversionDetails =
                    this.formatForeignCurrencyConversionDetailsForGeneralInvoiceTemplates(
                      invoice,
                      this.currencyFormatSelectedObject.name,
                    );
                  const formattedReceipts = this.formatReceiptsForGeneralInvoiceTemplates(
                    invoice,
                    this.showPrepaymentIsChecked,
                  );
                  const formattedTaxExemption = this.formatTaxExemptionForGeneralInvoiceTemplates(invoice, {
                    taxExemptionFormat,
                  });
                  const formattedRefInvoices = this.formatRefInvoiceForGeneralInvoiceTemplates(invoice);

                  invoice = {
                    ...invoice,
                    ...formattedInvoiceHeader,
                    ...formattedHeaderInfo,
                    ...formattedContainerDetails,
                    ...formattedInvoiceCharges,
                    ...foreignCurrencyConversionDetails,
                    ...formattedReceipts,
                    ...formattedTaxExemption,
                    ...formattedRefInvoices,
                  };

                  break;
                }
              }
            }
            break;
        }

        formattedInvoiceDetails.push(invoice);
      }
      this.$fullscreenLoading();

      this.designerV3ReportFiles = templateFileNames;
      this.designerV3ReportData = formattedInvoiceDetails;

      // Set file name for download
      let filenameTemp = '';
      if (this.designerV3ReportData.length > 1) {
        // multiple invoices
        filenameTemp = `Invoice`;
      } else if (this.designerV3ReportData.length == 1) {
        // BRN = Booking Ref No
        // Exp = Booking Ref No [Job.BookingRefNo]
        // Imp = BL No [Job.BL_No]
        // If [ARInvoice.Job_No] is null or '' then BRN/BL No = ''.
        const invno = this.designerV3ReportData[0].inv_no ?? '';
        let jobno = this.designerV3ReportData[0].Job_no ?? '';
        let brn = this.designerV3ReportData[0].BookingRefNo ?? '';
        let blno = this.designerV3ReportData[0].BL_no ?? '';
        if (this.designerV3ReportData[0].MultiJob_List && this.designerV3ReportData[0].MultiJob_List.length > 1) {
          jobno = 'MultipleJobs';
          brn = '';
          blno = '';
        }
        // To remove empty strings from the file name
        filenameTemp = [invno, jobno, brn, blno].filter(Boolean).join('_');
      }
      this.downloadFileName = `${filenameTemp}_${new Date().formatString('yyyyMMddHHmm')}`;

      generalInvoiceDetails = {
        ...generalInvoiceDetails, // =============== hopefully this isn't needed anymore after the GetARInvoiceDetails API has been changed
        _showCompanyLetterHead: this.showCompanyLetterHeadIsChecked,
        _header: this.userDetails.CoHeaderFin,
        _companyName: this.userDetails.CoName,
        _documentName: this.documentTypeBeingPrinted === '0' ? 'Invoice' : 'DN',
        _signatureRequired: this.invoiceSignatureRequired,
        // _showEnclosure: this.showEnclosureChecked,
      };
      switch (
        this.footerImageSelectedObject.name //Auto option not in here.
      ) {
        case 'Image 1':
          generalInvoiceDetails._footer = this.userDetails.CoFooterFin;
          break;

        case 'Image 2':
          generalInvoiceDetails._footer = this.settingsSystemCoFooterFin2;
          break;

        case 'Image 3':
          generalInvoiceDetails._footer = this.settingsSystemCoFooterFin3;
          break;
      }
      this.designerV3SharedData = generalInvoiceDetails;

      this.showDesignerV3 = true;
    },

    // make sure these methods never modify the values passed in; instead, return the formatted values
    formatInvoiceHeaderCustomerDetails: function (invoice, attentionOverride, customerDetailsSegments) {
      // formats customer details based on which segment needs to be shown, and returns an array of formatted segments
      // expects the invoice details from the API, the value from the attention input, and the segments to show

      const customerDetails = [];

      if (customerDetailsSegments.includes('C')) {
        customerDetails.push(invoice.cu_number);
      }

      if (this.invoiceAddressFormat.includes('N')) {
        customerDetails.push(invoice.cu_name);
      }

      if (this.invoiceAddressFormat.includes('A')) {
        const addressSegments = [
          invoice.cu_addr1 ?? '',
          invoice.cu_addr2 ?? '',
          invoice.cu_addr3 ?? '',
          invoice.cu_addr4 ?? '',
          `${invoice.Postcode ?? ''} ${invoice.City ?? ''}`.trim(),
          `${invoice.State ?? ''} ${invoice.Country ?? ''}`.trim(),
        ].filter(Boolean);

        customerDetails.push(...addressSegments);
      }

      const contactDetails = [];
      if (this.invoiceAddressFormat.includes('P') && invoice.cu_phone) {
        contactDetails.push(`Tel: ${invoice.cu_phone}`);
      }
      if (this.invoiceAddressFormat.includes('F') && invoice.cu_fax) {
        contactDetails.push(`Fax: ${invoice.cu_fax}`);
      }
      if (contactDetails.length > 0) {
        customerDetails.push(contactDetails.join('  '));
      }

      if (this.invoiceAddressFormat.includes('T')) {
        let resolvedAttention = '';
        if (attentionOverride) {
          resolvedAttention = `Attn.: ${attentionOverride}`;
        } else if (invoice.cu_contact) {
          resolvedAttention = `Attn.: ${invoice.cu_contact}`;
        }

        if (resolvedAttention) {
          customerDetails.push(resolvedAttention);
        }
      }

      customerDetails.push(`BRN: ${invoice.billcu_noregno}`);
      customerDetails.push(`SST No: ${invoice.billcu_nosst}`);
      customerDetails.push(`TIN No.: ${invoice.billcu_notin}`);

      return customerDetails;
    },
    formatInvoiceHeaderForGeneralInvoiceTemplates: async function (
      invoice,
      customerDetails,
      selectedCreditTerm,
      options,
    ) {
      // ================ this function should be usable by all the general invoice templates, i think they are all similar
      // only formats the invoice header details that aren't already hardcoded in the template
      // available options and each of their defaults: {
      //     documentNoFormatting: ("fillerOnly")|"bothFillerAndInvoiceNo",
      //     hidePrintedBy: false,
      //     printedByLabel: "Printed By",
      //     printedByValue: "",
      // }

      const formattedInvoiceHeader = {};

      if (invoice.filler) {
        if (options?.documentNoFormatting === 'bothFillerAndInvoiceNo') {
          formattedInvoiceHeader._formattedDocumentNo = `${invoice.filler} / ${invoice.inv_no}`;
        } else {
          formattedInvoiceHeader._formattedDocumentNo = invoice.filler;
        }
      } else {
        formattedInvoiceHeader._formattedDocumentNo = invoice.inv_no;
      }

      if (selectedCreditTerm) {
        formattedInvoiceHeader._formattedCreditTerm = selectedCreditTerm.creditTerm;
      } else if (invoice.CreditType === 'C') {
        formattedInvoiceHeader._formattedCreditTerm = invoice.CreditName;
      } else if (invoice.CreditTerm && invoice.CreditTerm !== '0') {
        formattedInvoiceHeader._formattedCreditTerm = `${invoice.CreditTerm} ${invoice.CreditName}${invoice.CreditTerm !== '1' ? 'S' : ''}`;
      }

      [
        // fill up the existing lines first, if there's still more then use the additional details
        formattedInvoiceHeader._cuDetailsLine1,
        formattedInvoiceHeader._cuDetailsLine2,
        formattedInvoiceHeader._cuDetailsLine3,
        formattedInvoiceHeader._cuDetailsLine4,
        formattedInvoiceHeader._cuDetailsLine5,
        formattedInvoiceHeader._cuDetailsLine6,
      ] = customerDetails.slice(0, 6);

      const additionalInvoiceHeader1 = [];
      if (!options?.hidePrintedBy) {
        const printedByValue = this.printedBySelection == '0' ? options?.printedByValue : invoice.UserID;
        additionalInvoiceHeader1.push({
          rightLabel: options?.printedByLabel ?? this.printedBySelection == '1' ? 'Prepared By' : 'Printed By',
          rightValue: printedByValue ?? '',
        });
      }
      if (this.invoiceShowOwnSSTNo) {
        additionalInvoiceHeader1.push({
          rightLabel: 'SST Reg No',
          rightValue: '{{sstno}}' 
        });
      }

      const additionalCustomerDetails = customerDetails.slice(6);
      let additionalInvoiceHeader1LinesUsed = 0;
      const additionalInvoiceHeader2 = [];
      for (const customerDetails of additionalCustomerDetails) {
        // for loop won't run if the array is empty, don't need to check if it is empty or not beforehand
        if (additionalInvoiceHeader1LinesUsed < additionalInvoiceHeader1.length) {
          additionalInvoiceHeader1[additionalInvoiceHeader1LinesUsed].leftValue = customerDetails;
          additionalInvoiceHeader1LinesUsed++;
        } else {
          additionalInvoiceHeader2.push({
            value: customerDetails,
          });
        }
      }

      if (additionalInvoiceHeader1.length > 0) {
        formattedInvoiceHeader._additionalInvoiceHeader1 = additionalInvoiceHeader1;
      }
      if (additionalInvoiceHeader2.length > 0) {
        formattedInvoiceHeader._additionalInvoiceHeader2 = additionalInvoiceHeader2;
      }

      if (invoice.QRURL) {
        formattedInvoiceHeader._QRImage = await this.$getQRURL(invoice.QRURL, { size: 150, margin: 10 });
      }

      return formattedInvoiceHeader;
    },
    formatHeaderInfoForGeneralFMSInvoiceTemplates: function (invoice, showHeaderInfo, options) {
      // only formats the header info fields that aren't already hardcoded in the template
      // available options and each of their defaults: {
      //     enclosureLocation: ("topOfHeaderInfo")|"belowChargesList"|"belowGrandTotal",
      //     showMultiJobTable: (true)|false
      // }
      const formattedHeaderInfo = {};

      if (!showHeaderInfo) {
        formattedHeaderInfo._hideJobDetails = true;
        formattedHeaderInfo._hideCommodityDetails = true;
        formattedHeaderInfo._hideBookingDetails = true;
        return formattedHeaderInfo;
      }

      if ((this.userDetails.CoRefname === 'HW' || this.userDetails.CoRefname == 'HWPG') && invoice.Job_Type === 'WAREH') { //cater specifically for HW
        formattedHeaderInfo._hideJobDetails = true;
        formattedHeaderInfo._showEnclosure = false;
        formattedHeaderInfo._hideCommodityDetails = true;
        formattedHeaderInfo._hideBookingDetails = false;
      }

      let enclosures = [invoice.Enclosure1, invoice.Enclosure2, invoice.Enclosure3].filter(Boolean);
      if ((this.userDetails.CoRefname === 'HW' || this.userDetails.CoRefname == 'HWPG') && invoice.Job_Type === 'WAREH') { //cater specifically for HW
        enclosures = [invoice.jobremark];
        formattedHeaderInfo._enclosuresConcatenated = invoice.jobremark;
      }
      if (enclosures.length > 0) {
        formattedHeaderInfo._enclosuresConcatenated = enclosures.join('\n');

        switch (options?.enclosureLocation) {
          case 'belowChargesList':
            formattedHeaderInfo._showChargesListRemarks = true;
            break;

          case 'belowGrandTotal':
            formattedHeaderInfo._showGrandTotalRemarks = true;
            break;

          default:
            formattedHeaderInfo._showHeaderRemarks = true;
            break;
        }
      } 

      if (!invoice.Job_no) {
        formattedHeaderInfo._hideJobDetails = true;
        formattedHeaderInfo._hideCommodityDetails = true;
        formattedHeaderInfo._hideBookingDetails = true;
        if (options?.showMultiJobTable && invoice.MultiJob_List && invoice.MultiJob_List[0]['job_type'] === 'I') {
          formattedHeaderInfo._showImpMultiJobTbl = true;
        } else if (
          options?.showMultiJobTable &&
          invoice.MultiJob_List &&
          invoice.MultiJob_List[0]['job_type'] === 'E'
        ) {
          formattedHeaderInfo._showExpMultiJobTbl = true;
        }

        if (formattedHeaderInfo._showImpMultiJobTbl || formattedHeaderInfo._showExpMultiJobTbl) {
          formattedHeaderInfo.invoiceJobs = invoice.MultiJob_List.map((j, index) => {
            return {
              ...j,
              date_eta: new Date(j.date_eta).formatString('yyyy-MM-dd'),
              num: index + 1,
            };
          });
        }

        return formattedHeaderInfo;
      }

      formattedHeaderInfo._transModeValue =
        invoice.trans === '1' ?
          `${invoice.MotherVName} / ${invoice.JobVessel_name} / ${invoice.VoyNo}`
        : `${invoice.JobVessel_name} / ${invoice.VoyNo}`;

      switch (invoice.TransMode) {
        case 'A':
          formattedHeaderInfo._BLLabel = 'AWB No.';
          break;

        case 'R':
          formattedHeaderInfo._BLLabel = 'D/O No.';
          break;

        default:
          formattedHeaderInfo._BLLabel = 'B/L No.';
          break;
      }
      if (invoice.HBL_no) {
        formattedHeaderInfo._BLLabel = `HBL / ${formattedHeaderInfo._BLLabel}`;
        formattedHeaderInfo._BLValue = `${invoice.HBL_no} / ${invoice.BL_no}`;
      } else {
        formattedHeaderInfo._BLValue = invoice.BL_no;
      }

      if (!invoice.GoodsDescrpt) {
        formattedHeaderInfo._hideCommodityDetails = true;
      }

      if (!invoice.BookingRefNo) {
        formattedHeaderInfo._hideBookingDetails = true;
      }

      return formattedHeaderInfo;
    },
    formatHeaderInfoForGeneralHMSInvoiceTemplates: function (invoice, showHeaderInfo, options) {
      // only formats the header info fields that aren't already hardcoded in the template
      // available options and each of their defaults: {
      //     enclosureLocation: ("topOfHeaderInfo")|"aboveChargesList"|"belowChargesList"|"belowGrandTotal",
      //     showSendToAddress: (false)|true
      // }

      const formattedHeaderInfo = {};
      formattedHeaderInfo._showSendToAddr = options?.showSendToAddress ?? false;

      if (this.invoiceHMSShowBNL) {
        formattedHeaderInfo._showBookingAndLorryDetails = true;
      }

      if (this.invoiceHMSShowBLNoBookingRef) {
        formattedHeaderInfo._showBookingAndBLNoDetails = true;

        if (invoice.Job_Type == 'I') {
          formattedHeaderInfo.BLBookingRefcaption = 'BL No';
          formattedHeaderInfo.BLBookingRef = invoice.BL_no;

        } else if (invoice.Job_Type == 'E') {
          formattedHeaderInfo.BLBookingRefcaption = 'Booking Ref No';
          formattedHeaderInfo.BLBookingRef = invoice.BookingRefNo;
        } else {
          formattedHeaderInfo.BLBookingRefcaption = '';
        }
      }

      if (!showHeaderInfo) {
        formattedHeaderInfo._hideJobDetails = true;
        formattedHeaderInfo._hideConsignmentDetails = true;
        formattedHeaderInfo._showSendToAddr = false;
        formattedHeaderInfo._hideVoyageDetails = true;
        formattedHeaderInfo._hideCommodityDetails = true;
        return formattedHeaderInfo;
      }

      const enclosures = [invoice.Enclosure1, invoice.Enclosure2, invoice.Enclosure3].filter(Boolean);
      if (enclosures.length > 0) {
        formattedHeaderInfo._enclosuresConcatenated = enclosures.join('\n');

        switch (options?.enclosureLocation) {
          // ================ not possible yet, need some changes to designer v3
          // case "aboveChargesList":
          //   break;

          case 'belowChargesList':
            formattedHeaderInfo._showChargesListBottomRemarks = true;
            break;

          case 'belowGrandTotal':
            formattedHeaderInfo._showGrandTotalRemarks = true;
            break;

          default:
            formattedHeaderInfo._showHeaderRemarks = true;
            break;
        }
      }

      if (options?.showSendToAddress && invoice.DestCompany) {
        const dest = [
          invoice.DestCompany,
          invoice.DestAddr1,
          invoice.DestAddr2,
          invoice.DestAddr3,
          invoice.DestAddr4,
        ].filter(Boolean);
        const postcodeCityState = [invoice.DestPostCode, invoice.DestCity, invoice.DestState].filter(Boolean);
        let sendToAddr = `${dest.join('<br>')}`;
        if (postcodeCityState.length > 0) {
          sendToAddr += `<br>${postcodeCityState.join(' ')}`;
        }
        formattedHeaderInfo._sendToAddr = sendToAddr;
      }

      if (!invoice.Job_no) {
        formattedHeaderInfo._hideJobDetails = true;
        formattedHeaderInfo._hideConsignmentDetails = true;
        formattedHeaderInfo._showSendToAddr = false;
        formattedHeaderInfo._hideVoyageDetails = true;
        formattedHeaderInfo._hideCommodityDetails = true;
      }

      return formattedHeaderInfo;
    },
    formatHeaderInfoForGeneralHMSMultiJobInvoiceTemplates: function (invoice, showHeaderInfo, options) {
      // only formats the header info fields that aren't already hardcoded in the template
      // available options and each of their defaults: {
      //     enclosureLocation: ("topOfHeaderInfo")|"aboveChargesList",
      // }

      const formattedHeaderInfo = {};

      if (!showHeaderInfo) {
        formattedHeaderInfo._hideHeaderInfo = true;
        return formattedHeaderInfo;
      }

      const enclosures = [invoice.Enclosure1, invoice.Enclosure2, invoice.Enclosure3].filter(Boolean);
      if (enclosures.length > 0) {
        formattedHeaderInfo._enclosuresConcatenated = enclosures.join('\n');

        switch (options?.enclosureLocation) {
          // ================ not possible yet, need some changes to designer v3
          // case "aboveChargesList":
          //   break;

          default:
            formattedHeaderInfo._showHeaderRemarks = true;
            break;
        }
      }

      if (!invoice.Job_no) {
        formattedHeaderInfo._hideHeaderInfo = true;
      }

      return formattedHeaderInfo;
    },
    formatHeaderInfoForGeneralTMSInvoiceTemplates: function (invoice, showHeaderInfo, options) {
      // only formats the header info fields that aren't already hardcoded in the template
      // available options and each of their defaults: {
      //     enclosureLocation: ("topOfHeaderInfo")|"belowChargesList",
      // }
      const formattedHeaderInfo = {};

      if (!showHeaderInfo) {
        formattedHeaderInfo._hideJobDetails = true;
        return formattedHeaderInfo;
      }

      const enclosures = [invoice.Enclosure1, invoice.Enclosure2, invoice.Enclosure3].filter(Boolean);
      if (enclosures.length > 0) {
        formattedHeaderInfo._enclosuresConcatenated = enclosures.join('\n');

        switch (options?.enclosureLocation) {
          case 'belowChargesList':
            formattedHeaderInfo._showChargesListRemarks = true;
            break;

          default:
            formattedHeaderInfo._showHeaderRemarks = true;
            break;
        }
      }

      if (!invoice.Job_no) {
        formattedHeaderInfo._hideJobDetails = true;
      }

      return formattedHeaderInfo;
    },
    formatHeaderInfoForGeneralTMSColumnInvoiceTemplates: function (invoice, showHeaderInfo, options) {
      // only formats the header info fields that aren't already hardcoded in the template
      // available options and each of their defaults: {
      //     enclosureLocation: ("topOfHeaderInfo")|"belowChargesList",
      // }

      const formattedHeaderInfo = {};

      if (!showHeaderInfo) {
        return formattedHeaderInfo;
      }

      const enclosures = [invoice.Enclosure1, invoice.Enclosure2, invoice.Enclosure3].filter(Boolean);
      if (enclosures.length > 0) {
        formattedHeaderInfo._enclosuresConcatenated = enclosures.join('\n');

        switch (options?.enclosureLocation) {
          case 'belowChargesList':
            formattedHeaderInfo._showChargesListRemarks = true;
            break;

          default:
            formattedHeaderInfo._showHeaderRemarks = true;
            break;
        }
      }

      return formattedHeaderInfo;
    },
    formatContainerDetailsForGeneralInvoiceTemplates: function (invoice) {
      // ================ this function should be usable by all the general invoice templates, i think they are all similar

      const formattedContainerDetails = {};

      if (!invoice.Cont_List) {
        formattedContainerDetails._hideContainerDetails = true;
        return formattedContainerDetails;
      }

      const containerList = invoice.Cont_List.map((container) => `${container.cont_no} (${container.cont_size}')`);
      [
        formattedContainerDetails._container1,
        formattedContainerDetails._container2,
        formattedContainerDetails._container3,
        formattedContainerDetails._container4,
      ] = containerList.splice(0, 4);

      if (containerList.length === 0) {
        return formattedContainerDetails;
      }

      formattedContainerDetails._additionalContainerDetails = [];
      while (containerList.length > 0) {
        const [container1, container2, container3, container4] = containerList.splice(0, 4);
        formattedContainerDetails._additionalContainerDetails.push({
          container1,
          container2,
          container3,
          container4,
        });
      }

      return formattedContainerDetails;

      // ===================== HMS invoice template displays the sum of all container types, even though if some of them are 0
      // ===================== for now, standardise them to use the total container count returned by the API instead, and see if any users complain
      // [
      //   formattedContainerDetails._containerCount20,
      //   formattedContainerDetails._containerCount40,
      //   formattedContainerDetails._containerCount45
      // ] = invoice.Cont_List.reduce((containerCount, currentContainer) => {
      //   switch (currentContainer.cont_size) {
      //     case "20":
      //       containerCount[0] += 1;
      //       break;

      //     case "40":
      //       containerCount[1] += 1;
      //       break;

      //     case "45":
      //       containerCount[2] += 1;
      //       break;
      //   }
      //   return containerCount;
      // }, [0, 0, 0]);
    },
    formatInvoiceChargesForGeneralFMSTaxInvoiceTemplate: function (invoice, selectedCurrencyFormat, remarksOnNewLine) {
      // formats everything needed for the entire charges table, from the header till grand total

      let showExchangeRateLabel = false;
      const showOriginalLocalAmount =
        this.invoiceShowMYRTotalCust.length > 0 && this.invoiceShowMYRTotalCust.includes(invoice.billcu_no);

      const formattedInvoiceCharges = invoice.Invoice_Charges.map((invoiceCharge) => {
        const formattedInvoiceCharge = { ...invoiceCharge };

        if (invoiceCharge.TaxCode) {
          formattedInvoiceCharge.formattedTaxRate = `${invoiceCharge.TaxCode}@${invoiceCharge.Tax}%`;
        } else {
          formattedInvoiceCharge.formattedTaxRate = `${invoiceCharge.Tax}%`;
        }

        if (remarksOnNewLine) {
          formattedInvoiceCharge.description = invoiceCharge.TxnType;
          formattedInvoiceCharge.nextLineRemarks = invoiceCharge.Remarks;
        } else if (invoiceCharge.Remarks) {
          formattedInvoiceCharge.description = `${invoiceCharge.TxnType} [${invoiceCharge.Remarks}]`;
        } else {
          formattedInvoiceCharge.description = invoiceCharge.TxnType;
        }

        if (invoiceCharge.Qty === '0') {
          formattedInvoiceCharge.qty = ''; // hide Qty if it's 0
          formattedInvoiceCharge.unitPrice = ''; // unit price is defined as amount excluding tax divided by qty, which is a problem when Qty is 0
        } else {
          formattedInvoiceCharge.qty = this.$currencyFormat(invoiceCharge.Qty, this.qtyDecimalPlaces);
          formattedInvoiceCharge.unitPrice = this.$currencyFormat(invoiceCharge.UnitPrice, this.unitPriceDecimalPlaces);
        }

        if (
          selectedCurrencyFormat === 'Local Currency' &&
          invoiceCharge.currency !== 'MYR' && // ============== hardcode as MYR first, should be reading local currency from somewhere instead
          invoiceCharge.currency !== invoice.currency
        ) {
          formattedInvoiceCharge.formattedRate = `${invoiceCharge.rate} ${invoiceCharge.currency}`;
          showExchangeRateLabel = true;
        } else if (showOriginalLocalAmount) {
          formattedInvoiceCharge.formattedRate = invoiceCharge.localamtexcltax;
        }

        formattedInvoiceCharge.amtExclTax = this.$currencyFormat(invoiceCharge.AmtExclTax);
        formattedInvoiceCharge.taxAmt = this.$currencyFormat(invoiceCharge.TaxAmt);
        formattedInvoiceCharge.amtInclTax = this.$currencyFormat(invoiceCharge.AmtInclTax);

        return formattedInvoiceCharge;
      });

      const formattedInvoiceChargesDetails = {
        _grandTotalExclTax: 0,
        _grandTotalTaxAmt: 0,
        _grandTotalInclTax: 0,

        _invoiceChargesGroups: this.groupInvoiceCharges(formattedInvoiceCharges),
        _formattedInvoiceCharges: formattedInvoiceCharges, // some companies don't want the charges to be reordered and grouped by tax rate, assign the ungrouped formatted invoice charges to a separate key for those cases

        _totalAmtInNumerals: this.$currencyFormat(invoice.AmtInclTax_Total), // may not be the same as the underscore grand total, this one has been deducted with the prepayment
      };

      for (const invoiceChargesGroup of formattedInvoiceChargesDetails._invoiceChargesGroups) {
        invoiceChargesGroup.formattedSubTotalAmtExclTax = this.$currencyFormat(invoiceChargesGroup.subTotalAmtExclTax);
        invoiceChargesGroup.formattedSubTotalTaxAmt = this.$currencyFormat(invoiceChargesGroup.subTotalTaxAmt);
        invoiceChargesGroup.formattedSubTotalAmtInclTax = this.$currencyFormat(invoiceChargesGroup.subTotalAmtInclTax);

        formattedInvoiceChargesDetails._grandTotalExclTax += invoiceChargesGroup.subTotalAmtExclTax;
        formattedInvoiceChargesDetails._grandTotalTaxAmt += invoiceChargesGroup.subTotalTaxAmt;
        formattedInvoiceChargesDetails._grandTotalInclTax += invoiceChargesGroup.subTotalAmtInclTax;
      }
      formattedInvoiceChargesDetails._grandTotalExclTax = this.$currencyFormat(
        formattedInvoiceChargesDetails._grandTotalExclTax,
      );
      formattedInvoiceChargesDetails._grandTotalTaxAmt = this.$currencyFormat(
        formattedInvoiceChargesDetails._grandTotalTaxAmt,
      );
      formattedInvoiceChargesDetails._grandTotalInclTax = this.$currencyFormat(
        formattedInvoiceChargesDetails._grandTotalInclTax,
      );

      if (showExchangeRateLabel) {
        formattedInvoiceChargesDetails._exchRateLabel = 'Exch Rate';
      } else if (showOriginalLocalAmount) {
        formattedInvoiceChargesDetails._exchRateLabel = 'Amt Excl Tax (MYR)';
      }

      return formattedInvoiceChargesDetails;
    },
    formatInvoiceChargesForGeneralFMSNonTaxInvoiceTemplate: function (invoice, remarksOnNewLine) {
      // formats everything needed for the entire charges table, from the header till grand total

      const groupedInvoiceCharges = this.groupInvoiceCharges(invoice.Invoice_Charges);
      const flattenedInvoiceCharges = [];

      for (const invoiceChargesGroup of groupedInvoiceCharges) {
        invoiceChargesGroup.invoiceCharges = invoiceChargesGroup.invoiceCharges.map((invoiceCharge) => {
          const formattedInvoiceCharge = {};

          if (remarksOnNewLine) {
            formattedInvoiceCharge.description = `${invoiceCharge.TxnType}<br>${invoiceCharge.Remarks}`;
          } else if (invoiceCharge.Remarks) {
            formattedInvoiceCharge.description = `${invoiceCharge.TxnType} [${invoiceCharge.Remarks}]`;
          } else {
            formattedInvoiceCharge.description = invoiceCharge.TxnType;
          }

          if (invoiceCharge.Qty === '0') {
            formattedInvoiceCharge.qty = ''; // hide Qty if it's 0
            formattedInvoiceCharge.unitPrice = ''; // unit price is defined as amount excluding tax divided by qty, which is a problem when Qty is 0
          } else {
            const qtyAsNumber = parseFloat(invoiceCharge.Qty);
            formattedInvoiceCharge.qty = this.$currencyFormat(qtyAsNumber, this.qtyDecimalPlaces);

            formattedInvoiceCharge.unitPrice = this.$currencyFormat(invoiceCharge.UnitPrice, this.unitPriceDecimalPlaces);
          }

          formattedInvoiceCharge.UOM = invoiceCharge.UOM;
          formattedInvoiceCharge.amount = parseFloat(invoiceCharge.AmtExclTax);
          formattedInvoiceCharge.formattedAmount = this.$currencyFormat(invoiceCharge.AmtExclTax);

          return formattedInvoiceCharge;
        });

        if (invoiceChargesGroup.taxRate !== '0') {
          invoiceChargesGroup.invoiceCharges.push({
            description: `${invoiceChargesGroup.taxRate}% GOVERNMENT SERVICE TAX`,
            amount: invoiceChargesGroup.subTotalTaxAmt,
            formattedAmount: this.$currencyFormat(invoiceChargesGroup.subTotalTaxAmt),
          });
        }

        flattenedInvoiceCharges.push(...invoiceChargesGroup.invoiceCharges);
      }

      const formattedInvoiceChargesDetails = {
        _invoiceCharges: flattenedInvoiceCharges,
        _grandTotalAmount: this.$currencyFormat(
          flattenedInvoiceCharges.reduce((sum, invoiceCharge) => (sum += invoiceCharge.amount), 0),
        ),
      };

      return formattedInvoiceChargesDetails;
    },
    formatInvoiceChargesForGeneralHMSTaxInvoiceTemplate: function (
      invoice,
      selectedCurrencyFormat,
      showExchRate,
      remarksOnNewLine,
    ) {
      // formats everything needed for the entire charges table, from the header till grand total

      let showExchangeRateLabel = false;
      const formattedInvoiceChargesDetails = {
        _grandTotalExclTax: 0,
        _grandTotalTaxAmt: 0,
        _grandTotalInclTax: 0,

        _invoiceChargesGroups: this.groupInvoiceCharges(invoice.Invoice_Charges),

        _totalAmtInNumerals: this.$currencyFormat(invoice.AmtInclTax_Total), // may not be the same as the underscore grand total, this one has been deducted with the prepayment
      };

      for (const invoiceChargesGroup of formattedInvoiceChargesDetails._invoiceChargesGroups) {
        invoiceChargesGroup.formattedSubTotalAmtExclTax = this.$currencyFormat(invoiceChargesGroup.subTotalAmtExclTax);
        invoiceChargesGroup.formattedSubTotalTaxAmt = this.$currencyFormat(invoiceChargesGroup.subTotalTaxAmt);
        invoiceChargesGroup.formattedSubTotalAmtInclTax = this.$currencyFormat(invoiceChargesGroup.subTotalAmtInclTax);

        formattedInvoiceChargesDetails._grandTotalExclTax += invoiceChargesGroup.subTotalAmtExclTax;
        formattedInvoiceChargesDetails._grandTotalTaxAmt += invoiceChargesGroup.subTotalTaxAmt;
        formattedInvoiceChargesDetails._grandTotalInclTax += invoiceChargesGroup.subTotalAmtInclTax;

        invoiceChargesGroup.invoiceCharges = invoiceChargesGroup.invoiceCharges.map((invoiceCharge) => {
          const formattedInvoiceCharge = {};

          if (invoiceCharge.TaxCode) {
            formattedInvoiceCharge.taxRate = `${invoiceCharge.TaxCode}@${invoiceCharge.Tax}%`;
          } else {
            formattedInvoiceCharge.taxRate = `${invoiceCharge.Tax}%`;
          }

          if (invoiceCharge.Remarks) {
            if (remarksOnNewLine) {
              formattedInvoiceCharge.description = invoiceCharge.TxnType;
              formattedInvoiceCharge.nextLineRemarks = invoiceCharge.Remarks;
            } else if (invoiceCharge.Remarks) {
              formattedInvoiceCharge.description = `${invoiceCharge.TxnType} [${invoiceCharge.Remarks}]`;
            } else {
              formattedInvoiceCharge.description = invoiceCharge.TxnType;
            }
          } else {
            formattedInvoiceCharge.description = invoiceCharge.TxnType;
          }

          // hide Qty if it's 0
          formattedInvoiceCharge.qty =
            invoiceCharge.Qty === '0' ? '' : this.$currencyFormat(invoiceCharge.Qty, this.qtyDecimalPlaces);

          formattedInvoiceCharge.UOM = invoiceCharge.UOM;
          formattedInvoiceCharge.unitPrice = this.$currencyFormat(invoiceCharge.UnitPrice, this.unitPriceDecimalPlaces);
          formattedInvoiceCharge.amtExclTax = this.$currencyFormat(invoiceCharge.AmtExclTax);
          formattedInvoiceCharge.taxAmt = this.$currencyFormat(invoiceCharge.TaxAmt);
          formattedInvoiceCharge.amtInclTax = this.$currencyFormat(invoiceCharge.AmtInclTax);

          if (
            showExchRate &&
            selectedCurrencyFormat === 'Local Currency' &&
            invoiceCharge.currency !== 'MYR' && // ============== hardcode as MYR first, should be reading local currency from somewhere instead
            invoiceCharge.currency !== invoice.currency
          ) {
            formattedInvoiceCharge.formattedRate = `${invoiceCharge.rate} ${invoiceCharge.currency}`;
            showExchangeRateLabel = true;
          }

          // If the rebate amount is not 0, show the rebate amount
          formattedInvoiceCharge.RebateAmt =
            invoiceCharge.RebateAmt && invoiceCharge.RebateAmt !== '0.00' ? invoiceCharge.RebateAmt : '';

          return formattedInvoiceCharge;
        });
      }

      formattedInvoiceChargesDetails._grandTotalExclTax = this.$currencyFormat(
        formattedInvoiceChargesDetails._grandTotalExclTax,
      );
      formattedInvoiceChargesDetails._grandTotalTaxAmt = this.$currencyFormat(
        formattedInvoiceChargesDetails._grandTotalTaxAmt,
      );
      formattedInvoiceChargesDetails._grandTotalInclTax = this.$currencyFormat(
        formattedInvoiceChargesDetails._grandTotalInclTax,
      );

      // Add "Exchange Rate" title if settingssystem has exchange rate enabled
      formattedInvoiceChargesDetails._exchRateLabel = showExchangeRateLabel ? 'Exch Rate' : '';

      // Add "Rebate" title if any charge has rebate
      const hasRebate = formattedInvoiceChargesDetails._invoiceChargesGroups.some((group) =>
        group.invoiceCharges.some((charge) => charge.RebateAmt !== ''),
      );
      formattedInvoiceChargesDetails.isRebateTitle = hasRebate ? 'Rebate' : '';

      return formattedInvoiceChargesDetails;
    },
    formatInvoiceChargesForGeneralHMSMultiJobTaxInvoiceTemplate: function (invoice) {
      // formats everything needed for the entire charges table, from the header till grand total

      const formattedInvoiceChargesDetails = {
        _grandTotalHaulage: 0,
        _grandTotalOthers: 0,
        _grandTotalToll: 0,
        _grandTotalFAF: 0,
        _grandTotalVBS: 0, // For EJ's HMS MultiJob Invoice report only
        _grandTotalDGC: 0, // For EJ's HMS MultiJob Invoice report only
        _grandTotalRebate: 0,
        _grandTotalExclTax: 0,
        _grandTotalTaxAmt: 0,
        _grandTotalInclTax: 0,

        _invoiceCharges: [],

        _totalAmtInNumerals: this.$currencyFormat(invoice.AmtInclTax_Total), // may not be the same as the underscore grand total, this one has been deducted with the prepayment
      };

      for (const [index, invoiceCharge] of invoice.Invoice_Charges.entries()) {
        formattedInvoiceChargesDetails._grandTotalHaulage += parseFloat(invoiceCharge.haulage);
        formattedInvoiceChargesDetails._grandTotalOthers += parseFloat(invoiceCharge.others);
        formattedInvoiceChargesDetails._grandTotalToll += parseFloat(invoiceCharge.toll);
        formattedInvoiceChargesDetails._grandTotalFAF += parseFloat(invoiceCharge.faf);
        formattedInvoiceChargesDetails._grandTotalVBS += parseFloat(invoiceCharge.vbs); // For EJ's HMS MultiJob Invoice report only
        formattedInvoiceChargesDetails._grandTotalDGC += parseFloat(invoiceCharge.dgc); // For EJ's HMS MultiJob Invoice report only
        formattedInvoiceChargesDetails._grandTotalRebate += parseFloat(invoiceCharge.rebate);
        formattedInvoiceChargesDetails._grandTotalExclTax += parseFloat(invoiceCharge.AmtExclTax);
        formattedInvoiceChargesDetails._grandTotalTaxAmt += parseFloat(invoiceCharge.TaxAmt);
        formattedInvoiceChargesDetails._grandTotalInclTax += parseFloat(invoiceCharge.AmtInclTax);

        const formattedInvoiceCharge = {};

        formattedInvoiceCharge.seq = index + 1; // For EJ's HMS MultiJob Invoice report only

        formattedInvoiceCharge.ROT = invoiceCharge.rot;
        formattedInvoiceCharge.ShprCons = invoiceCharge.cu_number;

        formattedInvoiceCharge.container = invoiceCharge.containerno;
        formattedInvoiceCharge.size = `${invoiceCharge.contsize}${invoiceCharge.conttype}`; // For EJ's HMS MultiJob Invoice report only
        formattedInvoiceCharge.area = invoiceCharge.destchamareacode; // For EJ's HMS MultiJob Invoice report only
        formattedInvoiceCharge.customerRef = invoiceCharge.custref_orderno;

        formattedInvoiceCharge.jobNo = invoiceCharge.type_ref;
        formattedInvoiceCharge.contDetails = `${invoiceCharge.contsize}${invoiceCharge.conttype} / ${invoiceCharge.handlingtype} / ${invoiceCharge.deliverytype}`;
        formattedInvoiceCharge.delDate = invoiceCharge.deldate; // For EJ's HMS MultiJob Invoice report only
        formattedInvoiceCharge.delTo = invoiceCharge.destcompany; // For EJ's HMS MultiJob Invoice report only
        formattedInvoiceCharge.pudo = invoiceCharge.pudo; // For EJ's HMS MultiJob Invoice report only

        formattedInvoiceCharge.haulage = invoiceCharge.haulage;
        formattedInvoiceCharge.others = this.$currencyFormat(invoiceCharge.others);
        if (formattedInvoiceCharge.others[0] == '-') {
          formattedInvoiceCharge.others = `(${formattedInvoiceCharge.others.replace('-', '')})`;
        }
        formattedInvoiceCharge.toll = invoiceCharge.toll;
        formattedInvoiceCharge.FAF = invoiceCharge.faf;
        formattedInvoiceCharge.rebate = invoiceCharge.rebate;
        formattedInvoiceCharge.vbs = invoiceCharge.vbs; // For EJ's HMS MultiJob Invoice report only
        formattedInvoiceCharge.dgc = invoiceCharge.dgc; // For EJ's HMS MultiJob Invoice report only
        formattedInvoiceCharge.amtExclTax = this.$currencyFormat(invoiceCharge.AmtExclTax);
        formattedInvoiceCharge.taxAmt = this.$currencyFormat(invoiceCharge.TaxAmt);
        formattedInvoiceCharge.amtInclTax = this.$currencyFormat(invoiceCharge.AmtInclTax);

        if (invoiceCharge.othchgremarks) {
          formattedInvoiceCharge.othChgLabel = 'Other Charges :';
          formattedInvoiceCharge.othChgRemarks = invoiceCharge.othchgremarks;
        }

        formattedInvoiceChargesDetails._invoiceCharges.push(formattedInvoiceCharge);
      }

      formattedInvoiceChargesDetails._grandTotalHaulage = this.$currencyFormat(
        formattedInvoiceChargesDetails._grandTotalHaulage || 0,
      );
      formattedInvoiceChargesDetails._grandTotalOthers = this.$currencyFormat(
        formattedInvoiceChargesDetails._grandTotalOthers || 0,
      );
      if (formattedInvoiceChargesDetails._grandTotalOthers[0] == '-') {
        formattedInvoiceChargesDetails._grandTotalOthers = `(${formattedInvoiceChargesDetails._grandTotalOthers.replace('-', '')})`;
      }
      formattedInvoiceChargesDetails._grandTotalToll = this.$currencyFormat(
        formattedInvoiceChargesDetails._grandTotalToll || 0,
      );
      formattedInvoiceChargesDetails._grandTotalFAF = this.$currencyFormat(
        formattedInvoiceChargesDetails._grandTotalFAF || 0,
      );
      formattedInvoiceChargesDetails._grandTotalVBS = this.$currencyFormat(
        formattedInvoiceChargesDetails._grandTotalVBS || 0,
      ); // For EJ's HMS MultiJob Invoice report only
      formattedInvoiceChargesDetails._grandTotalDGC = this.$currencyFormat(
        formattedInvoiceChargesDetails._grandTotalDGC || 0,
      ); // For EJ's HMS MultiJob Invoice report only
      formattedInvoiceChargesDetails._grandTotalRebate = this.$currencyFormat(
        formattedInvoiceChargesDetails._grandTotalRebate || 0,
      );
      formattedInvoiceChargesDetails._grandTotalExclTax = this.$currencyFormat(
        formattedInvoiceChargesDetails._grandTotalExclTax || 0,
      );
      formattedInvoiceChargesDetails._grandTotalTaxAmt = this.$currencyFormat(
        formattedInvoiceChargesDetails._grandTotalTaxAmt || 0,
      );
      formattedInvoiceChargesDetails._grandTotalInclTax = this.$currencyFormat(
        formattedInvoiceChargesDetails._grandTotalInclTax || 0,
      );

      return formattedInvoiceChargesDetails;
    },
    formatInvoiceChargesForGeneralTMSTaxInvoiceTemplate: function (invoice) {
      // formats everything needed for the entire charges table, from the header till grand total

      const formattedInvoiceChargesDetails = {
        _grandTotalExclTax: 0,
        _grandTotalTaxAmt: 0,
        _grandTotalInclTax: 0,

        _invoiceCharges: [],

        _totalAmtInNumerals: this.$currencyFormat(invoice.AmtInclTax_Total), // may not be the same as the underscore grand total, this one has been deducted with the prepayment
      };

      invoice.Invoice_Charges.forEach((invoiceCharge, chargeIndex) => {
        formattedInvoiceChargesDetails._grandTotalExclTax += parseFloat(invoiceCharge.AmtExclTax);
        formattedInvoiceChargesDetails._grandTotalTaxAmt += parseFloat(invoiceCharge.TaxAmt);
        formattedInvoiceChargesDetails._grandTotalInclTax += parseFloat(invoiceCharge.AmtInclTax);

        const formattedInvoiceCharge = {};

        formattedInvoiceCharge.index = chargeIndex + 1;

        if (invoiceCharge.TaxCode) {
          formattedInvoiceCharge.formattedTaxRate = `${invoiceCharge.TaxCode}@${invoiceCharge.Tax}%`;
        } else {
          formattedInvoiceCharge.formattedTaxRate = `${invoiceCharge.Tax}%`;
        }

        let tmsDescription = `${invoiceCharge.TxnType}`;
        if (this.invoiceTMSDescriptionFormat.includes('R') && invoiceCharge.Remarks) {
          tmsDescription += `\n${invoiceCharge.Remarks}`;
        }

        if (this.invoiceTMSDescriptionFormat.includes('D') && invoiceCharge.chamarea) {
          tmsDescription += `\nDestination: ${invoiceCharge.chamarea}`;
        }

        if (this.invoiceTMSDescriptionFormat.includes('C') && invoiceCharge.jobcustsname) {
          tmsDescription += `\nConsignee: ${invoiceCharge.jobcustsname}`;
        }

        if (invoiceCharge.departurecompany && invoiceCharge.company) {
          tmsDescription += `\nPick Up: ${invoiceCharge.departurecompany}\nDrop Off: ${invoiceCharge.company}`;
        }

        formattedInvoiceCharge.jobDate = invoiceCharge.jobdate ?? '';
        formattedInvoiceCharge.jobNo = invoiceCharge.jobno ?? '';
        formattedInvoiceCharge.description = tmsDescription;
        // formattedInvoiceCharge.DONum = invoiceCharge.rotno;
        formattedInvoiceCharge.DONum = invoiceCharge.requestno ?? '';
        formattedInvoiceCharge.deliveryDate = invoiceCharge.jobdate;
        formattedInvoiceCharge.vehicle = invoiceCharge.vehiclecode;
        formattedInvoiceCharge.qty =
          invoiceCharge.Qty === '0' ? '' : this.$currencyFormat(invoiceCharge.Qty, this.qtyDecimalPlaces);
        formattedInvoiceCharge.UOM = invoiceCharge.UOM;
        formattedInvoiceCharge.unitPrice = this.$currencyFormat(invoiceCharge.UnitPrice, this.unitPriceDecimalPlaces);
        formattedInvoiceCharge.amtExclTax = this.$currencyFormat(invoiceCharge.AmtExclTax);
        formattedInvoiceCharge.taxAmt = this.$currencyFormat(invoiceCharge.TaxAmt);
        formattedInvoiceCharge.amtInclTax = this.$currencyFormat(invoiceCharge.AmtInclTax);

        formattedInvoiceChargesDetails._invoiceCharges.push(formattedInvoiceCharge);
      });

      formattedInvoiceChargesDetails._grandTotalExclTax = this.$currencyFormat(
        formattedInvoiceChargesDetails._grandTotalExclTax,
      );
      formattedInvoiceChargesDetails._grandTotalTaxAmt = this.$currencyFormat(
        formattedInvoiceChargesDetails._grandTotalTaxAmt,
      );
      formattedInvoiceChargesDetails._grandTotalInclTax = this.$currencyFormat(
        formattedInvoiceChargesDetails._grandTotalInclTax,
      );

      return formattedInvoiceChargesDetails;
    },
    formatInvoiceChargesForGeneralTMSColumnTaxInvoiceTemplate: function (invoice) {
      // formats everything needed for the entire charges table, from the header till grand total

      const formattedInvoiceChargesDetails = {
        _grandTotalExclTax: 0,
        _grandTotalTaxAmt: 0,
        _grandTotalInclTax: 0,

        _invoiceCharges: [],

        _totalAmtInNumerals: this.$currencyFormat(invoice.AmtInclTax_Total), // may not be the same as the underscore grand total, this one has been deducted with the prepayment
      };

      for (const invoiceCharge of invoice.Invoice_Charges) {
        formattedInvoiceChargesDetails._grandTotalExclTax += parseFloat(invoiceCharge.AmtExclTax);
        formattedInvoiceChargesDetails._grandTotalTaxAmt += parseFloat(invoiceCharge.TaxAmt);
        formattedInvoiceChargesDetails._grandTotalInclTax += parseFloat(invoiceCharge.AmtInclTax);

        const formattedInvoiceCharge = {};

        formattedInvoiceCharge.taxRate = `${invoiceCharge.Tax}%`;
        formattedInvoiceCharge.date = invoiceCharge.jobdate;
        formattedInvoiceCharge.DOnum = invoiceCharge.rotno;
        formattedInvoiceCharge.destination = invoiceCharge.chamarea;
        formattedInvoiceCharge.remarks = invoiceCharge.jobremarks;
        formattedInvoiceCharge.amtExclTax = this.$currencyFormat(invoiceCharge.AmtExclTax);
        formattedInvoiceCharge.taxAmt = this.$currencyFormat(invoiceCharge.TaxAmt);
        formattedInvoiceCharge.amtInclTax = this.$currencyFormat(invoiceCharge.AmtInclTax);

        formattedInvoiceChargesDetails._invoiceCharges.push(formattedInvoiceCharge);
      }

      formattedInvoiceChargesDetails._grandTotalExclTax = this.$currencyFormat(
        formattedInvoiceChargesDetails._grandTotalExclTax,
      );
      formattedInvoiceChargesDetails._grandTotalTaxAmt = this.$currencyFormat(
        formattedInvoiceChargesDetails._grandTotalTaxAmt,
      );
      formattedInvoiceChargesDetails._grandTotalInclTax = this.$currencyFormat(
        formattedInvoiceChargesDetails._grandTotalInclTax,
      );

      return formattedInvoiceChargesDetails;
    },
    formatForeignCurrencyConversionDetailsForGeneralInvoiceTemplates: function (invoice, selectedCurrencyFormat) {
      // ================ this function should be usable by all the general invoice templates, i think they are all similar

      const formattedForeignCurrencyConversionDetails = {};

      if (
        selectedCurrencyFormat === 'Original Currency' &&
        invoice.currency?.toUpperCase?.() !== 'MYR' &&
        invoice.GSTEffective === '1'
      ) {
        if (this.invoiceShowMYRTotalCust.length > 0 && this.invoiceShowMYRTotalCust.includes(invoice.billcu_no)) {
          formattedForeignCurrencyConversionDetails._showCurrencyConversionDetails = false;
          formattedForeignCurrencyConversionDetails._showTotalLocalMYR = true;
        } else {
          formattedForeignCurrencyConversionDetails._showCurrencyConversionDetails = true;
        }
        const currencyRate = invoice.Invoice_Charges[0].rate; // foreign currency invoice with only one currency, since the charges are all only in one currency, just take the first rate
        formattedForeignCurrencyConversionDetails._rate = currencyRate; // take from invoice instead of formatted invoice charges, because it's easier to access

        let grandTotalExclTax = 0;
        let grandTotalTaxAmt = 0;
        let grandTotalExclTaxLocal = 0;
        let grandTotalTaxLocal = 0;
        for (const invoiceCharge of invoice.Invoice_Charges) {
          grandTotalExclTax += parseFloat(invoiceCharge.AmtExclTax);
          grandTotalTaxAmt += parseFloat(invoiceCharge.TaxAmt);
          grandTotalExclTaxLocal += parseFloat(invoiceCharge.localamtexcltax ?? 0);
          grandTotalTaxLocal += parseFloat(invoiceCharge.localtaxamt ?? 0);
        }

        formattedForeignCurrencyConversionDetails._grandTotalExclTaxForeign = this.$currencyFormat(
          grandTotalExclTax * parseFloat(currencyRate),
        );
        formattedForeignCurrencyConversionDetails._grandTotalTaxAmtForeign = this.$currencyFormat(
          grandTotalTaxAmt * parseFloat(currencyRate),
        );
        formattedForeignCurrencyConversionDetails._grandTotalExclTaxLocal =
          this.$currencyFormat(grandTotalExclTaxLocal);
        formattedForeignCurrencyConversionDetails._grandTotalTaxLocal = this.$currencyFormat(grandTotalTaxLocal);
      }

      return formattedForeignCurrencyConversionDetails;
    },
    formatReceiptsForGeneralInvoiceTemplates: function (invoice, showPrepayment) {
      const formattedReceiptDetails = {};

      if (showPrepayment && invoice.Receipt?.length > 0) {
        formattedReceiptDetails._receipts = invoice.Receipt.map((receipt) => ({
          receiptDescription: `LESS PREPAYMENT [RT #: ${receipt.ReceiptNo} Date: ${receipt.ReceiptDate} Chq#: ${receipt.ChequeNo} Bank: ${receipt.ChqBank} ${receipt.CurrencyRate}]`,
          receiptAmt: this.$currencyFormat(receipt.Amount),
        }));
      }

      return formattedReceiptDetails;
    },
    formatTaxExemptionForGeneralInvoiceTemplates: function (invoice, options) {
      // ================ this function should be usable by all the general invoice templates, i think they are all similar
      // available options and each of their defaults: {
      //     taxExemptionFormat: ("tabular")|"textual",
      // }

      const formattedTaxExemptionDetails = {};

      const invoiceHasESVCharge = parseFloat(invoice.sumGSTTaxExempted) > 0; // invoice.Invoice_Charges.some((charge) => charge.TaxCode?.startsWith("ESV"));
      if (!invoiceHasESVCharge) {
        return formattedTaxExemptionDetails;
      }

      switch (options?.taxExemptionFormat) {
        case 'textual':
          formattedTaxExemptionDetails._showTextualTaxExemptionFormat = true;
          break;

        default:
          formattedTaxExemptionDetails._showTabularTaxExemptionFormat = true;
          break;
      }

      formattedTaxExemptionDetails._formattedAmtExclTax = this.$currencyFormat(invoice.AmtB4GST);
      formattedTaxExemptionDetails._formattedSumGSTTaxExempted = this.$currencyFormat(invoice.sumGSTTaxExempted);
      formattedTaxExemptionDetails._formattedTotalInclExemptedGST = this.$currencyFormat(invoice.TotalInclExemptedGST);

      return formattedTaxExemptionDetails;
    },
    formatRefInvoiceForGeneralInvoiceTemplates: function (invoice) {
      // ================ this function should be usable by all the general invoice templates, i think they are all similar

      if (!invoice.RefInv) {
        return {}; // return an empty object instead of null or undefined, so an error won't be thrown when spreading in the calling function
      }

      const formattedRefInvoices = {
        _PairedRefInv: [], // combine all ref invoices into pairs
      };

      for (let index = 0; index < invoice.RefInv.length; index++) {
        const refInvoice = invoice.RefInv[index];

        if (index % 2 === 0) {
          // even index
          formattedRefInvoices._PairedRefInv.push({
            // create a new pair
            'Ref Invoice No 1': refInvoice['Ref Invoice No'],
            'Date 1': refInvoice.Date,
            'Amount 1': this.$currencyFormat(refInvoice.Amount),
            'Ref Invoice No 2': '', // leave these blank for now
            'Date 2': '', // filled in when index is odd
            'Amount 2': '',
          });
        } else {
          // odd index
          const pairedRefInvIndex = Math.floor(index / 2); // update the pair created when index was even
          formattedRefInvoices._PairedRefInv[pairedRefInvIndex]['Ref Invoice No 2'] = refInvoice['Ref Invoice No'];
          formattedRefInvoices._PairedRefInv[pairedRefInvIndex]['Date 2'] = refInvoice.Date;
          formattedRefInvoices._PairedRefInv[pairedRefInvIndex]['Amount 2'] = this.$currencyFormat(refInvoice.Amount);
        }
      }

      return formattedRefInvoices;
    },

    formatInvoiceChargesForGeneralTNLSTaxInvoiceTemplate: function (invoiceCharges, remarksOnNewLine) {
      const groupedInvoiceCharges = this.groupInvoiceCharges(invoiceCharges, true);

      if (groupedInvoiceCharges) {
        const totalTaxCount = new Set(groupedInvoiceCharges.map(m => String(m.taxRate)));

        if (totalTaxCount.size < groupedInvoiceCharges.length) { //if there is same tax rate but different taxcode
          for (const tax of totalTaxCount) {
            let totalsubTotalTaxAmt = 0;
            groupedInvoiceCharges.forEach(group => {
              if (tax === group['taxRate']) {
                totalsubTotalTaxAmt += group['subTotalTaxAmt'];
                delete group['subTotalTaxAmt'];
              }
            });

            // eslint-disable-next-line prefer-const -- lastTaxObj is being changed 
            let lastTaxObj = groupedInvoiceCharges.findLast(f => f['taxRate'] == tax);
            lastTaxObj['subTotalTaxAmt'] = totalsubTotalTaxAmt;
            lastTaxObj['srvTaxWording'] = `${tax}% Service Tax`;
          }
        }
      }

      for (const invoiceChargesGroup of groupedInvoiceCharges) {
        invoiceChargesGroup.formattedSubTotalAmtExclTax = this.$currencyFormat(invoiceChargesGroup.subTotalAmtExclTax);
        if (invoiceChargesGroup.subTotalTaxAmt) {
          invoiceChargesGroup.formattedSubTotalTaxAmt = this.$currencyFormat(invoiceChargesGroup.subTotalTaxAmt);
        }
        invoiceChargesGroup.formattedSubTotalAmtInclTax = this.$currencyFormat(invoiceChargesGroup.subTotalAmtInclTax);

        invoiceChargesGroup.invoiceCharges = invoiceChargesGroup.invoiceCharges.map((invoiceCharge) => {
          const formattedInvoiceCharge = {};

          if (invoiceCharge.Remarks) {
            if (remarksOnNewLine) {
              formattedInvoiceCharge.description = `${invoiceCharge.TxnType}<br>     ${invoiceCharge.Remarks}`;
            } else {
              formattedInvoiceCharge.description = `${invoiceCharge.TxnType} [${invoiceCharge.Remarks}]`;
            }
          } else {
            formattedInvoiceCharge.description = invoiceCharge.TxnType;
          }
          formattedInvoiceCharge.formattedAmount = this.$currencyFormat(invoiceCharge.AmtExclTax);
          formattedInvoiceCharge.amtExclTax = this.$currencyFormat(invoiceCharge.AmtExclTax);
          formattedInvoiceCharge.taxAmt = this.$currencyFormat(invoiceCharge.TaxAmt);
          formattedInvoiceCharge.amtInclTax = this.$currencyFormat(invoiceCharge.AmtInclTax);

          return formattedInvoiceCharge;
        });
      }

      return groupedInvoiceCharges;
    },

    groupInvoiceCharges: function (invoiceCharges, withTaxCode) {
      // group charges with the same tax rate together, and calculate the total amounts for each group, expects the charges that comes from the API
      // depending on the invoice format used, these groups will be rendered in different ways
      // withTaxCode is to check if the grouping need to include the taxCode key as well 

      const invoiceChargesGroups = []; // contains a list of all the keys in groupedInvoiceCharges, used to store the order that the tax groups should appear in
      // objects inherently have no order to the records, worried that looping over Object.keys will cause issues in the future, which is why this array exists

      const groupedInvoiceCharges = {}; // key is the tax rate, value is an object containing the total amount including and excluding tax, and all charges within that tax rate
      // use an object instead of an array for better time complexity when adding charges to their respective groups

      for (const invoiceCharge of invoiceCharges) {
        // API already sorts the charges in the order they should appear in, just need to group them together by tax rate and calculate the totals
        let groupKey = invoiceCharge.Tax;
        if (withTaxCode) {
          groupKey += invoiceCharge.TaxCode;
        }
        if (!groupedInvoiceCharges[groupKey]) {
          invoiceChargesGroups.push(groupKey);
          groupedInvoiceCharges[groupKey] = {
            TaxCode: invoiceCharge.TaxCode,
            taxRate: invoiceCharge.Tax,
            subTotalAmtExclTax: 0,
            subTotalTaxAmt: 0,
            subTotalAmtInclTax: 0,
            invoiceCharges: [],
          };
        }

        groupedInvoiceCharges[groupKey].subTotalAmtExclTax += parseFloat(invoiceCharge.AmtExclTax);
        groupedInvoiceCharges[groupKey].subTotalTaxAmt += parseFloat(invoiceCharge.TaxAmt);
        groupedInvoiceCharges[groupKey].subTotalAmtInclTax += parseFloat(invoiceCharge.AmtInclTax);
        groupedInvoiceCharges[groupKey].invoiceCharges.push(invoiceCharge);
      }

      return invoiceChargesGroups.map((taxRate) => groupedInvoiceCharges[taxRate]);
    },
    designerV3ShareClicked: function (shareLink) {
      if (this.designerV3ReportData[0].cu_email) {
        // need an if statement, because calling split on an empty string still returns an array with an empty string as it's only element, which causes email input to have one blank email inputted
        this.emailInvoicePopupToMails = this.designerV3ReportData[0].cu_email
          .split(';')
          .map((email) => ({ email: email.trim(), valid: true })); // =========== only take emails from the first invoice,
      } else {
        this.emailInvoicePopupToMails = [];
      }
      this.emailInvoicePopupCCMails = [];
      this.emailInvoicePopupBCCMails = [];

      if (this.designerV3ReportData.length > 1) {
        // multiple invoices were previewed at once
        this.emailInvoicePopupSubjectInputValue = 'Invoices';

        this.emailInvoicePopupMessageText = `Invoice: ${shareLink}\n\n`;
        for (const invoiceData of this.designerV3ReportData) {
          this.emailInvoicePopupMessageText += `Invoice ${invoiceData.inv_no}\n`;

          const invoiceDocumentsRoute = this.$router.resolve(
            `/GetDocInv/${this.userDetails.LoginUserId}/${this.userDetails.ConfigCode}/${invoiceData.txnid}`,
          );
          this.emailInvoicePopupMessageText += `Documents: ${window.location.origin}${invoiceDocumentsRoute.href}\n\n`;
        }
      } else {
        // only one invoice was previewed
        this.emailInvoicePopupSubjectInputValue = `Invoice ${this.designerV3ReportData[0].inv_no}`; // put the invoice number in the email subject

        this.emailInvoicePopupMessageText = `Invoice: ${shareLink}\n`;

        const invoiceDocumentsRoute = this.$router.resolve(
          `/GetDocInv/${this.userDetails.LoginUserId}/${this.userDetails.ConfigCode}/${this.designerV3ReportData[0].txnid}`,
        );
        this.emailInvoicePopupMessageText += `Documents: ${window.location.origin}${invoiceDocumentsRoute.href}\n`;
      }

      this.showEmailInvoicePopup = true;
    },
    designerV3AfterPrint: async function () {
      const requestBody = {
        report: 'INVOICE',
        coshortname: this.userDetails.CoRefname,
        internalcopy: this.internalCopyOnlyIsChecked ? '1' : '0',
        invoices: this.designerV3ReportData.map((reportData) => ({
          recordid: reportData.inv_no,
          invtype: 'IN', // ============ hardcoded for now, need to change to DN for debit note and CN for credit note
        })),
      };

      const response = await this.$apiCallV2('/PrintARInvoice/UpdatePrintCount', requestBody);
      if (response !== '') {
        // successful API calls should return an empty string, if it return anything else, assume an error occurred
        this.$showAlert(
          'error',
          'Failed to update print count',
          response?.toString() ?? 'An error occured when trying to update print count',
          [{ text: 'OK' }],
        );
      }
    },
    emailInvoicePopupButtonClicked: async function (buttonText) {
      if (buttonText === 'Cancel') {
        this.showEmailInvoicePopup = false;
        return;
      }

      if (this.emailInvoicePopupToMails.filter((mail) => mail.valid).length === 0) {
        this.$showAlert('error', 'No Valid Emails', 'Please enter at least one valid To email', [{ text: 'OK' }]);
        return;
      }

      if (!this.emailInvoicePopupSubjectInputValue.trim()) {
        const proceedWithoutSubject = await this.$showAlert(
          'warning',
          'No Subject',
          'No subject was entered\nProceed to email without a subject?',
          [{ text: 'Proceed' }, { text: 'Cancel', color: 'lightgray' }],
        );
        if (proceedWithoutSubject === 'Cancel') {
          return;
        }
      }

      const requestBody = {
        emaillist: [
          {
            doctype: 'Invoice',
            docno: this.designerV3ReportData.map((invoiceData) => invoiceData.inv_no).join(','), // users can change the selection before clicking on share, need to get invoice numbers from designer v3 data instead, which is what was used to render the report being shared
            docnofieldname: '',
            recipient: this.emailInvoicePopupToMails.map((mail) => mail.email).join(';'),
            cc: this.emailInvoicePopupCCMails
              .map((mail) => mail.email)
              .concat([this.userDetails.Email])
              .join(';'), // add the current user's email into CC
            bcc: this.emailInvoicePopupBCCMails.map((mail) => mail.email).join(';'),
            subject: this.emailInvoicePopupSubjectInputValue.trim(),
            message:
              this.emailInvoicePopupMessageText + `\nSender: ${this.userDetails.UserName} (${this.userDetails.Email})`, // add the current user's info in the email body
          },
        ],
      };

      this.$fullscreenLoading('Sending Email');
      const sendEmailResponse = await this.$apiCallV2('/PrintARInvoice/SendEmail', requestBody);
      this.$fullscreenLoading();

      if (sendEmailResponse?.trim?.()?.endsWith?.('queue to send.')) {
        // ================= maybe find a better way in the future
        this.$showAlert('success', 'Email successfully sent', 'Your email has been successfully sent', [
          { text: 'OK' },
        ]);
        this.showEmailInvoicePopup = false;
      } else {
        this.$showAlert('error', 'Email not sent', 'Your email could not be sent. Please try again.', [{ text: 'OK' }]);
      }
    },
  },
  mounted() {
    const invoiceFiltersPromises = [];
    let previewInvoicePromises = [];
    let promisesFulfilled = 0;

    const currentDate = new Date();
    let startDate = new Date(currentDate);
    startDate.setDate(-30);
    startDate = startDate.formatString('yyyy-MM-dd');
    const endDate = currentDate.formatString('yyyy-MM-dd');
    invoiceFiltersPromises.push(
      this.$apiCallV2(
        `/PrintARInvoice/GetInvoiceListing?startdate=${startDate}&enddate=${endDate}&txntype=${this.documentTypeBeingPrinted}`,
      ).then((result) => {
        this.invoiceNoItems = result;
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    invoiceFiltersPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CUSTOMER' }).then((result) => {
        this.customerItems = result;
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    invoiceFiltersPromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'jobtypeconfig', jobtype: 'I' }).then((result) => {
        this.invoiceJobTypeItems = result;
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    // =============== check if this default changes based on company or settingssystem
    this.containerListSelectedObject = this.containerListItems.find(
      (containerList) => containerList.name === 'All Container related to Job',
    );

    previewInvoicePromises.push(
      // Max Container Header
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=FINAR_MaxContHdr').then((result) => {
        const settingsSystemMaxContHeader = result[0].SettingsValue;
        if (settingsSystemMaxContHeader && !isNaN(parseInt(settingsSystemMaxContHeader))) {
          this.maxContNoInputValue = settingsSystemMaxContHeader;
        }
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    previewInvoicePromises.push(
      this.$apiCallV2('/Master/GetListMaster', { masterfile: 'CONTDELIVERYTYPE' }).then((result) => {
        this.deliveryTypeItems = result;
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    // =============== check if this default changes based on company or settingssystem
    this.currencyFormatSelectedObject = this.currencyFormatItems.find(
      (currencyFormat) => currencyFormat.name === 'Original Currency',
    );

    previewInvoicePromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=FIN_CreditTerm').then((result) => {
        const finCreditTermSettingsSystem = result[0].SettingsValue;
        if (finCreditTermSettingsSystem) {
          const finCreditTermSplit = finCreditTermSettingsSystem.split(',');
          this.creditTermItems = finCreditTermSplit.map((creditTerm) => ({
            creditTerm,
          }));
        }
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    previewInvoicePromises.push(
      // Company Letter Head
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=LetterHead_IN').then((result) => {
        const settingsSystemLetterHead = result[0].SettingsValue?.toUpperCase?.() ?? '';
        if (settingsSystemLetterHead) {
          this.showCompanyLetterHeadIsChecked = settingsSystemLetterHead === 'TRUE' || settingsSystemLetterHead === 'Y';
        }
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    previewInvoicePromises.push(
      // Header Info
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=JobInfo_IN').then((result) => {
        const settingsSystemJobInfo = result[0].SettingsValue?.toUpperCase?.() ?? '';
        if (settingsSystemJobInfo) {
          this.showHeaderInfoIsChecked = settingsSystemJobInfo === 'TRUE' || settingsSystemJobInfo === 'Y';
        }
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    previewInvoicePromises.push(
      // Prepayment Info
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=FINAR_ShowPrePay').then((result) => {
        const settingsSystemShowPrePay = result[0].SettingsValue?.toUpperCase?.() ?? '';
        if (settingsSystemShowPrePay) {
          this.showPrepaymentIsChecked = settingsSystemShowPrePay === 'TRUE' || settingsSystemShowPrePay === 'Y';
        }
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    previewInvoicePromises.push(
      // Remark Newline Info
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=FINAR_RemarkFmt').then((result) => {
        const settingsSystemRemarkFormat = result[0].SettingsValue?.toUpperCase?.() ?? '';
        if (settingsSystemRemarkFormat) {
          this.remarkPrintNextLineIsChecked = settingsSystemRemarkFormat === 'NEXTLINE';
        }
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    previewInvoicePromises.push(
      //Show Enclosure
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=6GInvDefShowEncl').then((result) => {
        const settingsSystemShowEnclosure = result[0].SettingsValue?.toUpperCase?.() ?? '';
        if (settingsSystemShowEnclosure) {
          this.showEnclosureChecked = settingsSystemShowEnclosure === 'TRUE';
        }
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    previewInvoicePromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=FIN_InvInternalCopy').then((result) => {
        // show the checkboxes if the returned value is truthy
        const settingsSystemFINInvInternalCopy = result[0].SettingsValue?.toUpperCase?.() ?? '';
        if (settingsSystemFINInvInternalCopy) {
          this.showInternalCopyOnlyCheckbox =
            settingsSystemFINInvInternalCopy === 'TRUE' || settingsSystemFINInvInternalCopy === 'Y';
        }
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    previewInvoicePromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=FIN_InvGSTFormat').then((result) => {
        const finInvGSTFormat = result[0].SettingsValue;
        const matchingFormat = this.formatItems.find((formatItem) => formatItem.APIValue === finInvGSTFormat);
        if (matchingFormat) {
          this.formatSelectedObject = matchingFormat;
        }
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    const signatureRequiredSettingsItem = this.documentTypeBeingPrinted === '0' ? 'Sign_IN' : 'Sign_DN';
    previewInvoicePromises.push(
      this.$apiCallV2(`/Master/GetSettingsSystem?settingsitem=${signatureRequiredSettingsItem}`).then((result) => {
        const settingsSystemSignatureRequired = result[0].SettingsValue?.toUpperCase?.() ?? '';
        if (settingsSystemSignatureRequired) {
          this.invoiceSignatureRequired =
            settingsSystemSignatureRequired === 'TRUE' || settingsSystemSignatureRequired === 'Y';
        }
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    previewInvoicePromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=6GInvRptAddrFormat').then((result) => {
        const settingsSystemInvRptAddrFormat = result[0].SettingsValue ?? '';
        if (settingsSystemInvRptAddrFormat) {
          this.invoiceAddressFormat = settingsSystemInvRptAddrFormat;
        }
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    previewInvoicePromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=6G_InvTaxExptFormat').then((result) => {
        const settingsSystemInvTaxExptFormat = result[0].SettingsValue ?? '';
        if (settingsSystemInvTaxExptFormat) {
          this.invoiceTaxExemptionFormat = settingsSystemInvTaxExptFormat;
        }
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    previewInvoicePromises.push(
      this.$apiCallV2('/Login/GetDetails').then((result) => {
        this.userDetails = result;
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    previewInvoicePromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=CoFooterFin2').then((result) => {
        this.settingsSystemCoFooterFin2 = result[0].SettingsValue ?? '';
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    previewInvoicePromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=CoFooterFin3').then((result) => {
        this.settingsSystemCoFooterFin3 = result[0].SettingsValue ?? '';
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    const footerImageDescriptionPromises = [
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=CoFooterFinDesc1').then((result) => {
        const image1 = this.footerImageItems.find((footerImage) => footerImage.name === 'Image 1');
        image1.description = result[0].SettingsValue || image1.name;
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=CoFooterFinDesc2').then((result) => {
        const image2 = this.footerImageItems.find((footerImage) => footerImage.name === 'Image 2');
        image2.description = result[0].SettingsValue || image2.name;
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=CoFooterFinDesc3').then((result) => {
        const image3 = this.footerImageItems.find((footerImage) => footerImage.name === 'Image 3');
        image3.description = result[0].SettingsValue || image3.name;
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=6GInvAutoFooter').then((result) => {
        this.autoFooterConditions = result[0].SettingsValue ?? '';
        if (this.autoFooterConditions != '') {
          this.footerImageItems.push({ name: 'Auto', description: 'Auto' });
        }
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      })
    ];
    Promise.allSettled(footerImageDescriptionPromises).then((_promiseOutcomes) => {
      // only populate footer image selection after all descriptions have been loaded, otherwise description will be empty
      if (this.autoFooterConditions != '') {
        this.footerImageSelectedObject = this.footerImageItems.find((footerImage) => footerImage.name === 'Auto');
      } else {
        this.footerImageSelectedObject = this.footerImageItems.find((footerImage) => footerImage.name === 'Image 1');
      }
    });
    previewInvoicePromises = previewInvoicePromises.concat(footerImageDescriptionPromises); // footer image description promises also need to be fulfilled before invoices can be previewed

    const enclosureLocSettingsItem =
      this.documentTypeBeingPrinted === '0' ? '6GInvRptEnclosureLoc' : '6GDNRptEnclosureLoc';
    previewInvoicePromises.push(
      this.$apiCallV2(`/Master/GetSettingsSystem?settingsitem=${enclosureLocSettingsItem}`).then((result) => {
        const settingsSystemInvEnclosureLoc = result[0].SettingsValue ?? '';
        if (settingsSystemInvEnclosureLoc) {
          this.invoiceEnclosureLoc = settingsSystemInvEnclosureLoc;
        }
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    previewInvoicePromises.push(
      this.$apiCallV2(`/Master/GetSettingsSystem?settingsitem=6GInvRptMultiJobTbl`).then((result) => {
        const settingsSystemInvMultiJobTable = result[0].SettingsValue ?? '';
        if (settingsSystemInvMultiJobTable) {
          this.invoiceMultiJobTable = settingsSystemInvMultiJobTable == '1';
        }
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    previewInvoicePromises.push(
      this.$apiCallV2(`/Master/GetSettingsSystem?settingsitem=6GInvRptQtyDecimals`).then((result) => {
        this.qtyDecimalPlaces = parseInt(result[0].SettingsValue || 3);
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    previewInvoicePromises.push(
      this.$apiCallV2('/Master/GetSettingsSystem?settingsitem=6GUnitPriceDecimals').then((result) => {
        this.unitPriceDecimalPlaces = parseInt(result[0].SettingsValue || 2);
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    previewInvoicePromises.push(
      this.$apiCallV2(`/Master/GetSettingsSystem?settingsitem=6GInvRptShowSendAddr`).then((result) => {
        this.invoiceShowSendToAddress = result[0].SettingsValue == '1';
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    previewInvoicePromises.push(
      this.$apiCallV2(`/Master/GetSettingsSystem?settingsitem=6G_ShowMYRInOri`).then((result) => {
        const temp = result[0].SettingsValue ?? '';
        if (temp) {
          this.invoiceShowMYRTotalCust = temp.split(',');
        }
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    // HMS
    previewInvoicePromises.push(
      this.$apiCallV2(`/Master/GetSettingsSystem?settingsitem=6GInvRptHMSShowERate`).then((result) => {
        this.invoiceHMSShowExchRate = result[0].SettingsValue == '1';
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    previewInvoicePromises.push(
      this.$apiCallV2(`/Master/GetSettingsSystem?settingsitem=6GInvRptHMSShowBNL`).then((result) => {
        this.invoiceHMSShowBNL = result[0].SettingsValue == '1';
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    previewInvoicePromises.push(
      this.$apiCallV2(`/Master/GetSettingsSystem?settingsitem=6GInvRptHMSShowBBLNo`).then((result) => {
        this.invoiceHMSShowBLNoBookingRef = result[0].SettingsValue == '1';
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    //TMS
    previewInvoicePromises.push(
      this.$apiCallV2(`/Master/GetSettingsSystem?settingsitem=6GInvRptTMSDetFormat`).then((result) => {
        this.invoiceTMSDescriptionFormat = result[0].SettingsValue ? result[0].SettingsValue : 'RDC';
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    previewInvoicePromises.push(
      this.$apiCallV2(`/Master/GetSettingsSystem?settingsitem=6G_RptPrintedBy`).then((result) => {
        this.printedBySelection = result[0].SettingsValue ? result[0].SettingsValue : '0';
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    previewInvoicePromises.push(
      this.$apiCallV2(`/Master/GetSettingsSystem?settingsitem=6G_InvHidePrintedBy`).then((result) => {
        this.invoiceHidePrintedBy = result[0].SettingsValue ? result[0].SettingsValue == '1' : false;
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );

    previewInvoicePromises.push(
      this.$apiCallV2(`/Master/GetSettingsSystem?settingsitem=6G_InvShowOwnSSTNo`).then((result) => {
        this.invoiceShowOwnSSTNo = result[0].SettingsValue ? result[0].SettingsValue == '1' : false;
        this.$topLoading(++promisesFulfilled / (previewInvoicePromises.length + invoiceFiltersPromises.length));
      }),
    );


    let queryParamDocumentNo;
    if (this.documentTypeBeingPrinted === '0') {
      queryParamDocumentNo = this.$route.query.invno?.trim?.();
    } else {
      queryParamDocumentNo = this.$route.query.dnno?.trim?.();
    }

    if (queryParamDocumentNo) {
      this.invoiceNoIsChecked = true;
      this.invoiceNoStartSearch = queryParamDocumentNo; // don't worry about whether the API for the invoice list has completed or not at this point
      this.invoiceNoEndSearch = queryParamDocumentNo; // just populate the search value and call the API to get the filtered invoice list first
    } else {
      this.invoiceDateIsChecked = true; // users can then print an invoice without having to wait for the non blocking API calls to complete first
      this.invoiceDateStartValue = new Date();
      this.invoiceDateEndValue = new Date();
    }

    this.updateInvoiceFiltersSummary();
    const getFilteredInvoiceListPromise = this.getFilteredInvoiceList();

    Promise.allSettled([...invoiceFiltersPromises, getFilteredInvoiceListPromise]).then((_promiseOutcomes) => {
      if (queryParamDocumentNo) {
        const matchingInvoice = this.invoiceNoItems.find((invoice) => invoice['Invoice #'] === queryParamDocumentNo);
        if (matchingInvoice) {
          this.invoiceNoStartSelectedObject = matchingInvoice;
          this.invoiceNoEndSelectedObject = matchingInvoice;
        }
      }

      // only enable invoice filters button once all prerequisite data has been loaded and invoice list has been retrieved
      this.invoiceFiltersIsDisabled = false;
    });

    Promise.allSettled(previewInvoicePromises).then((_promiseOutcomes) => {
      this.previewDocumentRelatedControlsIsDisabled = false;
    });
  },
  watch: {},
};
</script>

<style lang="scss" scoped>
@import '@/css/all.scss';

.print-AR-invoice-main {
  font-size: 12px;
  width: 100%;
  height: 100%;
  padding: 10px 45px;
  display: flex;

  > .AR-invoice-options {
    margin-right: 2.5px;
    flex: 1;
    background-color: white;
    padding: 5px;

    > .invoice-filters-and-table-box-controls {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
      margin-bottom: 5px;

      > .invoice-filters-summary-container {
        margin-right: 5px;
        align-content: center; // vertically align the summary text
        text-align: end; // horizontally align the summary text
      }

      .button-filter-content-container {
        text-align: left;
        cursor: auto;

        .invoice-no-filter-container {
          margin-top: 15px;
        }

        .customer-filter-container {
          margin-top: 15px;
        }

        .outstanding-and-job-type-filters {
          display: flex;
          flex-direction: row;

          > .outstanding-dropdown {
            margin-right: 5px;
          }

          > .job-type-dropdown {
            margin-left: 5px;
          }
        }

        .not-printed-filter {
          margin-top: 5px;
          display: flex;
          justify-content: flex-start;
        }

        .refresh-button-container {
          margin-top: 10px;
          display: flex;
          justify-content: flex-end;
        }
      }

      > .select-deselect-all-button {
        margin-left: 5px;
      }
    }

    > .invoices-table-box-container {
      height: calc(100% - 300px);
      // height: 50%; // ============ change later
    }

    > .container-formatting-controls {
      margin-top: 5px;
      display: flex;
      flex-direction: row;

      > .container-list-dropdown {
        margin-right: 5px;
      }

      > .max-cont-input {
        margin-left: 5px;
      }
    }

    > .delivery-type-and-currency-formatting-controls {
      display: flex;
      flex-direction: row;

      > .delivery-type-dropdown {
        margin-right: 5px;
      }

      > .currency-format-dropdown {
        margin-left: 5px;
      }
    }

    > .credit-term-and-pay-to-formatting-controls {
      display: flex;
      flex-direction: row;

      > .credit-term-dropdown {
        margin-right: 5px;
        width: calc(50% - 5px);
      }
    }

    > .header-formatting-controls {
      display: flex;
      flex-direction: row;
      margin-top: 5px;

      > .header-formatting-column {
        width: 50%;
      }
    }

    > .invoice-format-controls {
      margin-top: 5px;

      > .format-dropdown {
        width: calc(50% - 5px);
      }
    }

    > .footer-image-controls {
      margin-top: 5px;

      > .footer-image-dropdown {
        width: calc(50% - 5px);
      }
    }

    > .call-to-action-buttons {
      display: flex;
      flex-direction: row;
      justify-content: flex-end;
    }
  }

  > .AR-invoice-preview {
    margin-left: 2.5px;
    flex: 1;
    background-color: white;
    padding: 5px;

    > .user-instructions {
      height: 100%;
      display: flex;
      text-align: center;
      justify-content: center;
      align-items: center;
      font-size: 20px;
    }
  }

  > .email-invoice-popup {
    .email-invoice-popup-content-container {
      display: flex;
      flex-direction: row;

      > .mail-inputs-container {
        margin-right: 5px;
        display: flex;
        flex-direction: column;
        flex: 1;

        > .email-input-container {
          margin-bottom: 5px;

          .note {
            color: gray;
            font-size: 0.85em;
          }
        }
      }

      > .email-content-inputs-container {
        margin-left: 5px;
        display: flex;
        flex-direction: column;
        flex: 2;
      }
    }
  }
}
</style>
